import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatUI from './ChatUI';
import AnalysisTypeChoice from './components/AnalysisTypeChoice';
import UpgradeAccountModal from './components/UpgradeAccountModal';
import ImageConsentModal from './components/ImageConsentModal';
import PlusPaywall from './components/PlusPaywall';
import OnboardingModal from './components/OnboardingModal';
import FirstTimeOverview, { clearHasSeenProductTour, getGuestProfile, clearGuestProfile } from './components/FirstTimeOverview';
import { getHasAskedDismissReason, setHasAskedDismissReason } from './components/DismissReasonSheet';
import ContextualSaveInvitation, { RegistrationDismissReason } from './components/ContextualSaveInvitation';
import ProfileCompletionNudge from './components/ProfileCompletionNudge';
import SuggestedQuestions from './components/SuggestedQuestions';
import axios from 'axios';
import { useAuth } from './hooks/useAuth';
import { supabase } from './supabase';
import config, { getChatRequestUrl, getColourAnalysisRequestUrl, getBillingUrl } from './config';
import { stripLeadingToolCallJson } from './utils/stripLeadingToolCallJson';
import { extractStructuredJsonObject, normalizeStructuredContent } from './utils/structuredReply';
import { IMAGE_ROLES, canPersistFullSkinResult } from './utils/imageJourney';
import { createRequestTimeout, CHAT_REQUEST_TIMEOUT_MS } from './utils/chatResilience';
import { reportError } from './utils/errorReporting';
import { getProductAnalysisContext } from './utils/productAnalysisContext';
import { NEXT_SAVE_PROMPT_KEY, nextSavePromptAfter, readNextSavePromptAt, recordAnonymousRequestCount } from './utils/registrationGate';
import {
  trackChatMessageSent,
  trackSkinAnalysisStart,
  trackSkinAnalysisComplete,
  trackSkinPhotoAnalysis,
  trackProductPhotoAnalysis,
  trackError,
  trackRegisterPromptShown,
  trackRegisterPromptDismissed,
  trackRegistrationAction,
  trackReadFeedback,
  trackCorrectionSubmitted,
  trackShadeMatchesRequested,
  trackConversationContinuation,
  trackRegisterDismissReason,
  trackConsentDeclined,
  trackOutcomeJourneyFailed,
  trackOutcomeResultDelivered,
} from './utils/analytics';

// Use getChatRequestUrl() at request time so on localhost we hit the Function URL (avoids API Gateway 404).

/**
 * Extracts the value of the "advice" field from a partially-built JSON buffer
 * that is being streamed. Returns a clean string (unescaped) so we can display
 * the advice progressively while the rest of the JSON is still arriving.
 */
function extractAdviceFromJsonBuffer(buffer) {
  const keyIdx = buffer.indexOf('"advice"');
  if (keyIdx === -1) return '';
  const afterKey = buffer.slice(keyIdx + 8);
  const m = afterKey.match(/\s*:\s*"([\s\S]*)/);
  if (!m) return '';
  const raw = m[1];
  let result = '';
  for (let i = 0; i < raw.length; i++) {
    if (raw[i] === '\\' && i + 1 < raw.length) {
      const next = raw[i + 1];
      if (next === 'n') { result += '\n'; i++; }
      else if (next === '"') { result += '"'; i++; }
      else if (next === '\\') { result += '\\'; i++; }
      else if (next === 't') { result += '\t'; i++; }
      else { result += raw[i]; }
    } else if (raw[i] === '"') {
      break;
    } else {
      result += raw[i];
    }
  }
  return result;
}

// Default welcome message shown when chat is empty (first thing user sees).
// 2026-07 onboarding revamp: cut from ~90 words to two lines. The old version
// explained the whole product before the user had done anything — the scan is
// the hook (83% of users act after it), so lead with it and price the effort
// ("15 seconds") rather than pitching features they haven't asked about yet.
const WELCOME_MESSAGE_OBJ = { text: `Hi, I'm Son:mi.

Start with a 15-second skin scan and I'll read your skin type, concerns and shade, or just ask me anything.`, sender: 'assistant', skipFeedback: true, isWelcome: true };

// All analysis (face and product) now uses ChatGPT via /chat endpoint

/**
 * Plays back structured message advice character-by-character so the UI
 * feels like streaming even though the full JSON arrives at once.
 * Returns a cleanup function that stops the animation.
 */
function typewriterAnimate(messageContent, setMessages, onDone) {
  if (typeof messageContent !== 'object' || !messageContent?.advice) {
    onDone?.();
    return () => {};
  }
  const fullText = messageContent.advice;
  let i = 0;
  // Render at ~30 chars/frame (≈ 60fps → ~1800 chars/sec, similar to GPT streaming)
  const CHARS_PER_TICK = 18;
  const TICK_MS = 10;

  // Start with CHARS_PER_TICK chars so the Advice tab appears immediately
  i = Math.min(CHARS_PER_TICK, fullText.length);
  const partialMsg = {
    ...messageContent,
    advice: fullText.slice(0, i),
    _streaming: true,
  };

  setMessages(prev => [...prev, { text: partialMsg, sender: 'assistant', isStructured: true, _streaming: true }]);

  const id = setInterval(() => {
    i += CHARS_PER_TICK;
    if (i >= fullText.length) {
      clearInterval(id);
      setMessages(prev => {
        const copy = [...prev];
        const last = { ...copy[copy.length - 1] };
        last.text = { ...messageContent };
        last._streaming = false;
        copy[copy.length - 1] = last;
        return copy;
      });
      onDone?.();
    } else {
      setMessages(prev => {
        const copy = [...prev];
        const last = { ...copy[copy.length - 1] };
        last.text = { ...last.text, advice: fullText.slice(0, i) };
        copy[copy.length - 1] = last;
        return copy;
      });
    }
  }, TICK_MS);
  return () => clearInterval(id);
}

const ChatComponent = ({ isAnonymous = false, autoStartScan = false, saveAfterFirstScan = false }) => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE_OBJ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamStatus, setStreamStatus] = useState('');
  const typewriterCleanupRef = React.useRef(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const colourJourneyEntryHandled = React.useRef(false);
  // Removed latestAnalysis state as component was removed
  const [chatType, setChatType] = useState('general'); // 'general', 'makeup', 'skincare', 'routine'
  const [uploadedImages, setUploadedImages] = useState([]); // Track multiple uploaded images
  const [analysisProgress, setAnalysisProgress] = useState(0); // Track analysis progress
  const [showAnalyzedPhotos, setShowAnalyzedPhotos] = useState(true); // Auto-hide after 30s
  const [userImagePrompt, setUserImagePrompt] = useState(''); // User's prompt for image analysis
  const [showAnalysisChoice, setShowAnalysisChoice] = useState(false); // Show analysis type choice modal
  const [pendingFiles, setPendingFiles] = useState([]); // Files waiting for analysis type selection
  const [pendingPrompt, setPendingPrompt] = useState(''); // User's prompt for pending images
  const previewUrls = useRef([]); // Track blob URLs for cleanup (prevent memory leaks)
  const messageCountSinceModal = useRef(0); // Track messages since last modal show
  const totalAnonMessages = useRef(0); // Track total assistant messages for mandatory gate
  const { getAuthSession } = useAuth();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeStartsOnSignIn, setUpgradeStartsOnSignIn] = useState(false);
  const [upgradeSkipsPitch, setUpgradeSkipsPitch] = useState(false);
  const [registrationInvite, setRegistrationInvite] = useState(null);
  // Once the gate has fired, a guest can dismiss it to re-read the conversation,
  // but typing is what it actually guards — so any attempt to compose brings it
  // back. Nothing is bypassed; the chat just stops being a locked screen.
  const gateArmed = useRef(false);
  // A rendered flag (the ref alone won't re-render the composer). While true the
  // composer is replaced by a 'create account' CTA, so the guest can close the
  // modal and read/browse but cannot keep chatting without registering.
  const [gateActive, setGateActive] = useState(false);
  const reopenGate = useCallback(() => {
    if (!gateArmed.current) return false;
    setRegistrationInvite('hard');
    return true;
  }, []);
  useEffect(() => {
    if (!showUpgradeModal) return undefined;
    const nudge = () => {
      try {
        const list = document.querySelector('.chat-messages-area');
        if (list) list.scrollTop = list.scrollHeight;
      } catch (_e) {}
    };
    const a = requestAnimationFrame(nudge);
    const b = setTimeout(nudge, 260);
    return () => { cancelAnimationFrame(a); clearTimeout(b); };
  }, [showUpgradeModal]);
  const [upgradeModalMandatory, setUpgradeModalMandatory] = useState(false);

  // Image consent state
  const [imageConsent, setImageConsent] = useState(null); // null = not checked yet, true/false
  const [showConsentModal, setShowConsentModal] = useState(false);
  // Son:mi+ paywall. Opened by the backend's {paywall:true} quota response,
  // never by the frontend guessing at counts — the server owns entitlement.
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallBusy, setPaywallBusy] = useState(false);
  const [pendingConsentFiles, setPendingConsentFiles] = useState(null); // files waiting for consent
  // Post-scan intake belonging to a file held behind the consent gate. Without
  // this the answers were silently dropped on the FIRST scan -- the exact path
  // this onboarding is for, since a new visitor hasn't granted consent yet.
  const [pendingConsentIntake, setPendingConsentIntake] = useState(null);
  const [pendingConsentJourney, setPendingConsentJourney] = useState(null);
  const [pendingConsentPrompt, setPendingConsentPrompt] = useState('');
  const consentResolveRef = useRef(null); // resolves requestConsent() promise (button-click flow)
  
  // Onboarding & Profile state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showProfileNudge, setShowProfileNudge] = useState(false);  // never init to true — always shown deliberately
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const [missingFields, setMissingFields] = useState([]);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  // Persist across refreshes so the nudge doesn't re-fire every session
  const [hasHadAnalysis, setHasHadAnalysis] = useState(() => {
    try { return localStorage.getItem('sonmi_had_analysis') === 'true'; } catch { return false; }
  });
  
  // Suggested Questions state
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  // What we already know for the post-scan intake (age + sensitivities). When set,
  // the scan intake modal skips asking and threads these straight into the analysis.
  const [scanIntakeKnown, setScanIntakeKnown] = useState(null);
  const [questionsTier, setQuestionsTier] = useState(1);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);

  // Product education tour for new users (shown once per device, all visitors)
  const [showProductTour, setShowProductTour] = useState(false);
  const [autoTriggerUpload, setAutoTriggerUpload] = useState(false);
  const emailSavePromptShown = useRef(false);
  useEffect(() => {
    if (autoStartScan) setAutoTriggerUpload(true);
  }, [autoStartScan]);
  // Menu state lives in ChatUI, but the registration modal lives here — so the welcome
  // screen asks for a destination and ChatUI picks it up, mirroring how autoTriggerUpload
  // already works. Lets "here's what it unlocks" actually take you somewhere.
  const [pendingMenuView, setPendingMenuView] = useState(null);

  // ?preview=welcome — review the post-registration screen without creating a real
  // account (dev and prod share the user database, so a throwaway signup is a real row).
  const previewWelcome = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('preview') === 'welcome';
  useEffect(() => {
    if (previewWelcome) setShowUpgradeModal(true);
  }, [previewWelcome]);

  // Called by the post-registration welcome. 'scan' isn't a menu view — it's the capture
  // flow — so it routes through the existing upload trigger instead.
  const handleExploreAfterRegister = (destination) => {
    setShowUpgradeModal(false);
    setUpgradeModalMandatory(false);
    if (destination === 'scan') setAutoTriggerUpload(true);
    else setPendingMenuView(destination);
  };
  // 2026-07 onboarding revamp: the 8-slide FirstTimeOverview tour no longer
  // fires. New users were being over-warmed — a welcome wall plus 8 slides (4 of
  // them questions) before they could touch the product. The outcome-led entry
  // now routes people straight into a concrete task, and age/sensitivities are
  // asked AFTER the scan, where they visibly sharpen the read.
  //
  // The component itself is intentionally left in place (unmounted) so its
  // question banks + guest-profile helpers stay importable and the flow can be
  // restored quickly if the funnel says otherwise.

  // Migrate guest profile to Supabase on sign-in
  useEffect(() => {
    if (!currentUserId || isAnonymous) return;
    const guestProfile = getGuestProfile();
    if (!guestProfile) return;
    supabase.from('user_profiles').upsert(
      { user_id: currentUserId, ...guestProfile },
      { onConflict: 'user_id' }
    ).then(() => clearGuestProfile());
  }, [currentUserId, isAnonymous]);
  
  // Track if logged-in user has incomplete profile and should see onboarding (used when tour closes)
  const hasIncompleteProfileRef = useRef(false);
  const recoveryMessageCountRef = useRef(0); // used when polling history after send error
  const streamReplyShownRef = useRef(false);  // so only one of (fetch response / poll) updates UI
  const messageBeingSentRef = useRef('');    // capture message for request so closure is never stale
  const sendingRef = useRef(false);          // prevent double-send (e.g. double-click or Strict Mode)
  const turnSettledRef = useRef(false);      // #47: a turn renders its answer once — recovery must not clobber/redraw it
  const activeRequestAbortRef = useRef(null); // release a stale browser connection after resume
  // The outcome picker gives an image a precise job. Keep that job attached to
  // the chat so later questions cannot silently fall back to a full-face read.
  const activeJourneyRef = useRef(null);

  // Multi-chat session management
  const currentSessionIdRef = useRef(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [chatList, setChatList] = useState([]);

  const journeyStorageKey = (sessionId = currentSessionIdRef.current) =>
    currentUserId && sessionId ? `sonmi_active_journey_${currentUserId}_${sessionId}` : null;

  const setActiveJourney = (journey) => {
    activeJourneyRef.current = journey || null;
    const key = journeyStorageKey();
    if (!key) return;
    try {
      if (journey) localStorage.setItem(key, JSON.stringify(journey));
      else localStorage.removeItem(key);
    } catch (_e) {}
  };

  const restoreActiveJourney = (sessionId) => {
    const key = journeyStorageKey(sessionId);
    if (!key) { activeJourneyRef.current = null; return; }
    try {
      activeJourneyRef.current = JSON.parse(localStorage.getItem(key) || 'null');
    } catch (_e) {
      activeJourneyRef.current = null;
    }
  };
  
  // Get current user ID from auth session
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const { userId } = await getAuthSession();
        setCurrentUserId(userId);
      } catch (error) {
        console.error('Error fetching user ID:', error);
        setCurrentUserId(null);
      }
    };
    fetchUserId();
  }, [getAuthSession]);

  // Load image consent status from DB (or localStorage for anon users)
  useEffect(() => {
    if (!currentUserId) return;

    const loadConsent = async () => {
      // For anonymous users, check localStorage.
      // Same rule as registered: both the flag AND a grant timestamp must be
      // present. This stops a stale value from a previous session bleeding
      // into a "new" profile on the same device.
      if (isAnonymous) {
        const stored = localStorage.getItem('sonmi_image_consent');
        const storedAt = localStorage.getItem('sonmi_image_consent_at');
        setImageConsent(stored === 'true' && !!storedAt);
        return;
      }
      // For registered users, check user_profiles.
      // Require BOTH the flag AND a grant timestamp. Without the timestamp we
      // can't prove the user ever explicitly granted — so we treat it as
      // un-consented and re-prompt. This is defensive against any legacy state
      // where the flag could be true without an audit trail.
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('image_consent, image_consent_at')
          .eq('user_id', currentUserId)
          .maybeSingle();
        const granted = data?.image_consent === true && !!data?.image_consent_at;
        setImageConsent(granted);
      } catch (err) {
        console.error('Error loading image consent:', err);
        setImageConsent(false);
      }
    };
    loadConsent();
  }, [currentUserId, isAnonymous]);

  // Grant image consent — persists to DB (or localStorage for anon)
  const grantImageConsent = async () => {
    setImageConsent(true);
    const now = new Date().toISOString();

    if (isAnonymous) {
      localStorage.setItem('sonmi_image_consent', 'true');
      localStorage.setItem('sonmi_image_consent_at', now);
    } else if (currentUserId) {
      try {
        await supabase.from('user_profiles').upsert(
          {
            user_id: currentUserId,
            image_consent: true,
            image_consent_at: now,
            image_consent_version: '1.0',
          },
          { onConflict: 'user_id' }
        );
      } catch (err) {
        console.error('Error saving image consent:', err);
      }
    }
  };

  // Check if user needs onboarding & fetch profile completeness
  useEffect(() => {
    if (!currentUserId || isAnonymous) {
      // Guests keep their basics in localStorage — mirror the scan-intake fields
      // so a returning guest with an age on file isn't re-asked either.
      try {
        const raw = localStorage.getItem('sonmi_guest_profile');
        const g = raw ? JSON.parse(raw) : null;
        setScanIntakeKnown(g?.age_range ? { age_range: g.age_range, sensitivities: g.sensitivities || [] } : null);
      } catch { setScanIntakeKnown(null); }
      return;
    }

    const checkOnboardingStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('age_range, climate, sensitivities, skin_tone, undertone, primary_concerns, skincare_goals, answered_questions')
          .eq('user_id', currentUserId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching profile:', error);
          return;
        }

        // Remember the post-scan intake basics so the modal can skip when we
        // already have them (age is the field the intake always collects).
        setScanIntakeKnown(data?.age_range ? { age_range: data.age_range, sensitivities: data.sensitivities || [] } : null);

        // Check if essential fields are missing
        const essentialFields = ['age_range', 'climate'];
        const missingEssential = essentialFields.filter(field => !data || !data[field]);

        // Calculate profile completeness (6 fields — skincare_goals removed, not in profile form)
        const allFields = [
          data?.age_range,
          data?.climate,
          data?.sensitivities?.length > 0,
          data?.skin_tone,
          data?.undertone,
          data?.primary_concerns?.length > 0,
        ];
        const filledCount = allFields.filter(f => typeof f === 'boolean' ? f : f && f !== '').length;
        const completenessPercent = Math.round((filledCount / allFields.length) * 100);

        // Don't overwrite a 100% value already set by handleOnboardingComplete —
        // the DB may still be mid-write and return stale data, causing a false nudge.
        setProfileCompleteness(prev => (prev === 100 ? 100 : completenessPercent));
        setMissingFields(missingEssential.map(f => f.replace('_', ' ')));

        const hasAnsweredAny = data?.answered_questions && Object.keys(data.answered_questions).length > 0;
        const shouldPromptOnboarding = missingEssential.length > 0 && !hasAnsweredAny && !hasCompletedOnboarding;
        hasIncompleteProfileRef.current = shouldPromptOnboarding;

        // Legacy onboarding modal removed — the FirstTimeOverview tour handles all new users.
        // For users who skipped the tour, the profile nudge fires after their first analysis.
      } catch (err) {
        console.error('Error checking onboarding status:', err);
      }
    };

    checkOnboardingStatus();
  }, [currentUserId, isAnonymous, hasCompletedOnboarding]);

  // Fetch suggested questions
  useEffect(() => {
    if (!currentUserId) return;  // anon users get suggestions too — cold traffic needs them most

    const fetchSuggestedQuestions = async () => {
      try {
        // Send the JWT so the backend resolves the real (logged-in) user — it
        // derives identity from the Authorization header only (post-hardening).
        let _sqTok = null;
        try { const _s = await getAuthSession(); _sqTok = _s?.tokens?.accessToken || null; } catch (_) {}
        const response = await axios.post(getChatRequestUrl(), {
          userId: currentUserId,
          getSuggestedQuestions: true
        }, _sqTok ? { headers: { 'Authorization': `Bearer ${_sqTok}` } } : {});


        if (response.data.questions) {
          setSuggestedQuestions(response.data.questions);
          setQuestionsTier(response.data.tier || 1);
        } else {
        }
      } catch (err) {
        console.error('❌ Error fetching suggested questions:', err);
      }
    };

    fetchSuggestedQuestions();
    // Refreshes after each turn via the messages.length dependency below — no timer needed.
  }, [currentUserId, isAnonymous, messages.length]);

  // Persist chatList to localStorage whenever it changes
  useEffect(() => {
    if (!currentUserId || chatList.length === 0) return;
    try {
      localStorage.setItem(`sonmi_chats_${currentUserId}`, JSON.stringify(chatList));
    } catch (e) { /* ignore storage errors */ }
  }, [chatList, currentUserId]);

  // Update the chatList entry for the current session whenever messages change
  useEffect(() => {
    if (!currentUserId || !currentSessionIdRef.current) return;
    const userMsgs = messages.filter(m => m.sender === 'user');
    if (userMsgs.length === 0) return;

    const firstUserMsg = userMsgs[0];
    const title = typeof firstUserMsg.text === 'string'
      ? firstUserMsg.text.slice(0, 45)
      : 'New chat';

    // Use the last plain-text AI message as preview (skip the welcome message)
    const aiMsgs = messages.filter(
      m => m.sender === 'assistant' &&
      typeof m.text === 'string' &&
      !m.text.startsWith("Hi! I'm your Son:mi")
    );
    const lastAI = aiMsgs[aiMsgs.length - 1];
    const preview = lastAI ? lastAI.text.slice(0, 65) : '';

    const sid = currentSessionIdRef.current;
    setChatList(prev => {
      const exists = prev.some(c => c.sessionId === sid);
      const entry = {
        sessionId: sid,
        title,
        preview,
        createdAt: exists
          ? (prev.find(c => c.sessionId === sid)?.createdAt || new Date().toISOString())
          : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const updated = exists
        ? prev.map(c => c.sessionId === sid ? entry : c)
        : [entry, ...prev];
      return updated.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    });
  }, [messages, currentUserId]); // intentionally excludes currentSessionId — we use the ref

  // Show profile nudge — only on second session or later, after an analysis
  useEffect(() => {
    if (!currentUserId || isAnonymous || showOnboardingModal || showProductTour) return;

    // Track whether this user has had a previous session
    const sessionKey = `sonmi_has_prior_session_${currentUserId}`;
    const hasPriorSession = localStorage.getItem(sessionKey) === 'true';

    // Mark that this session has now happened (so next time counts as prior)
    // We set it after a brief delay so same-session doesn't count
    const markSession = setTimeout(() => {
      try { localStorage.setItem(sessionKey, 'true'); } catch {}
    }, 30000); // 30s in — safe to call it a real session

    if (!hasPriorSession) return () => clearTimeout(markSession);

    // Check if an analysis has appeared in messages
    const hasAnalysisInMessages = messages.some(m =>
      m.sender === 'assistant' && (
        m.text?.includes?.('undertone') ||
        m.text?.includes?.('skin depth') ||
        m.text?.includes?.('analysis')
      )
    );

    if (hasAnalysisInMessages && !hasHadAnalysis && profileCompleteness < 75) {
      setHasHadAnalysis(true);
      try { localStorage.setItem('sonmi_had_analysis', 'true'); } catch {}
      // DISABLED 2026-07-24 (Sofia): the "Your profile is X% complete" nudge is
      // hidden for now. It fired 5s after an analysis and competed with the
      // register ask for attention at the same moment. The component and all its
      // handlers are left intact — re-enable by restoring this one line:
      //   setTimeout(() => setShowProfileNudge(true), 5000);
    }

    return () => clearTimeout(markSession);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, currentUserId, isAnonymous, hasHadAnalysis, profileCompleteness, showOnboardingModal, showProductTour]);

  // Handlers for onboarding and profile
  const handleOnboardingComplete = (data) => {
    setShowOnboardingModal(false);
    setHasCompletedOnboarding(true);
    // Set completeness to 100 immediately — the user just filled the profile.
    // Previously used prev+40 which was unreliable, and setting hasCompletedOnboarding
    // triggers checkOnboardingStatus which re-queries stale DB data and overwrites with
    // the old value, causing the profile nudge to incorrectly fire at e.g. 57%.
    setProfileCompleteness(100);
    setShowProfileNudge(false);
  };

  const handleOnboardingSkip = () => {
    setShowOnboardingModal(false);
    setHasCompletedOnboarding(true); // Mark as completed even if skipped
  };

  const handleProfileNudgeDismiss = () => {
    setShowProfileNudge(false);
    // Prevent re-showing on next page load — user already saw it
    try { localStorage.setItem('sonmi_had_analysis', 'true'); } catch {}
  };

  const handleCompleteProfile = () => {
    setShowProfileNudge(false);
    // Menu opening is handled by ChatUI via onOpenMenu prop (if provided)
  };

  const handleSuggestedQuestionClick = (questionText, source = 'suggested_question') => {
    // Pass as messageOverride so it sends immediately — bypasses the
    // setInput → state-propagation → sendMessage race (matches the CTA pattern).
    // The old sendMessage(null, questionText) sent nothing (2nd arg ignored).
    trackConversationContinuation(source);
    sendMessage(questionText);
  };

  // Global function to add product analysis to chat (called from ProductAnalysisButton)
  React.useEffect(() => {
    window.addProductAnalysisToChat = (analysisData) => {
      // analysisData should be an object with type: 'product_analysis'
      setMessages(prev => [
        ...prev,
        {
          text: analysisData, // Keep as object for ProductAnalysisCard rendering
          sender: 'assistant',
          timestamp: new Date().toISOString()
        }
      ]);
    };
    
    return () => {
      delete window.addProductAnalysisToChat;
    };
  }, []);

  // Function to save product to backend
  const saveProductToBackend = async (product, category, userId) => {
    try {
      // Authenticate like every other chat call. A stale/absent `userId` here was
      // making chat heart-saves silently no-op — the save POST never fired (or would
      // land under no user), so nothing ever reached the shelf. Pull a fresh session
      // for a valid uid + bearer token; the backend derives user_id from the JWT
      // first (app.py:4671), falling back to body.userId.
      let _accessToken = null;
      let _uid = userId;
      try {
        const _s = await getAuthSession();
        _accessToken = _s?.tokens?.accessToken || null;
        _uid = _s?.userId || userId;
      } catch (_) { /* fall back to the passed userId */ }
      if (!_uid) {
        console.error('❌ No userId - cannot save product to backend');
        return null;
      }
      const response = await axios.post(getChatRequestUrl(), {
        userId: _uid,
        saveProduct: {
          name: product.name,
          brand: product.brand,
          category: product.category, // ✅ FIXED: Use actual product category (e.g., "Serum", "Moisturizer")
          listCategory: category, // ✅ FIXED: Use this for which list to save to ("current" or "wishlist")
          image: product.image,
          rating: product.rating,
          notes: product.notes,
          ai_analysis: product.ai_analysis, // Include AI analysis if present
          // Recommended shade for foundations / concealers / blushes — null for everything else.
          recommended_shade: product.recommended_shade || product.recommendedShade || product.shade || null,
          // Shelf (#26): usage tags + swatch so chat saves land on the shelf with chips/swatch.
          usage_tags: product.usage_tags || product.usageTags || [],
          swatch_image_url: product.swatch_image_url || product.swatchImageUrl || null,
          swatch_hex: product.swatch_hex || product.swatchHex || null,
        }
      }, _accessToken ? { headers: { 'Authorization': `Bearer ${_accessToken}` } } : undefined);

      if (response.data.success) {
        return response.data.product;
      } else {
        console.error('❌ Failed to save product:', response.data.message);
        return null;
      }
    } catch (error) {
      console.error('❌ Error saving product to backend:', error);
      return null;
    }
  };

  // Function to get saved products from backend.
  //
  // Hard 30s timeout: prior to 2026-05-29 this had no `timeout` option, so an
  // unresponsive Lambda left the menu spinner ("loading…") spinning forever.
  // Sofia hit this on murex 2026-05-29 ("loading, loading, loading"). The
  // call goes through the Lambda Function URL (no API Gateway 29s cap), but
  // a cold-start + first catalog brand-norm scan + per-product signed URL
  // refresh can still occasionally exceed sensible UX bounds. 30s gives
  // cold starts room while bounding the worst case.
  const getSavedProductsFromBackend = async (userId) => {
    try {
      // Load under the SAME authenticated userId that SAVES use (getAuthSession), not a
      // stale/anonymous prop — otherwise saves land under your real account and the lists
      // load under an empty one and show nothing. Bearer header for parity with save.
      let _accessToken = null;
      let _uid = userId;
      try {
        const _s = await getAuthSession();
        _accessToken = _s?.tokens?.accessToken || null;
        _uid = _s?.userId || userId;
      } catch (_) {}
      const response = await axios.post(getChatRequestUrl(), {
        userId: _uid,
        getSavedProducts: true
      }, { timeout: 30000, ...(_accessToken ? { headers: { 'Authorization': `Bearer ${_accessToken}` } } : {}) });

      if (response.data.success) {
        return response.data.products;
      } else {
        console.error('❌ Failed to get saved products:', response.data.message);
        return null;
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        console.error('⏱️ getSavedProductsFromBackend: 30s timeout — Lambda likely cold-starting or catalog query slow');
      } else {
        console.error('❌ Error getting saved products from backend:', error);
      }
      return null;
    }
  };

  // Shared function to parse structured responses
  const tryParseStructured = (text) => {
    try {
      let cleaned = String(text).trim();
      cleaned = cleaned.replace(/^```[a-zA-Z]*\n/, '').replace(/\n```$/, '');

      let parsed = extractStructuredJsonObject(cleaned);
      if (!parsed && cleaned.startsWith('{')) {
        try {
          const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
          const blob = jsonMatch ? jsonMatch[0] : cleaned;
          parsed = JSON.parse(blob);
        } catch (_) {
          parsed = null;
        }
      }
      if (!parsed || typeof parsed !== 'object') return null;

      if (parsed.type === 'product_analysis') {
        return {
          type: 'product_analysis',
          data: {
            score: parsed.score || 0,
            compatibility: parsed.compatibility || 'medium',
            pros: parsed.pros || [],
            concerns: parsed.concerns || [],
            reasoning: parsed.reasoning || '',
            best_for: parsed.best_for || 'General use',
            ingredients_highlight: parsed.ingredients_highlight || '',
            ingredient_insight: parsed.ingredient_insight || '',
            key_ingredients: parsed.key_ingredients || []
          },
          productName: parsed.name || 'Product',
          productBrand: parsed.brand || '',
          productImage: null
        };
      }

      const structKeys = ['advice', 'products', 'routine', 'general'];
      if (structKeys.some((k) => Object.prototype.hasOwnProperty.call(parsed, k))) {
        return normalizeStructuredContent(parsed);
      }
    } catch (e) {
    }
    return null;
  };

  // New function for product recommendations
  const requestProductRecommendations = async (productTypes = ['foundation']) => {
    try {
      const { tokens, userId } = await getAuthSession();
      const accessToken = tokens?.accessToken;

      if (!accessToken || !userId) {
        throw new Error('No access token or userId found');
      }


      // Add loading message
      setMessages(prev => [
        ...prev,
        { text: 'Finding product recommendations for you...', sender: 'assistant' }
      ]);

      // Get latest skin analysis and request products
      const response = await axios.post(getChatRequestUrl(), {
        userId: userId,
        request_products: true,
        product_types: productTypes,
        use_existing_analysis: true  // Use their latest skin analysis
      }, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      // Remove loading message and add recommendations
      setMessages(prev => {
        const withoutLoading = prev.slice(0, -1);
        const formattedRecommendations = formatProductRecommendations(response.data.product_recommendations);
        
        // Only add message if we have valid recommendations
        if (formattedRecommendations) {
          return [
            ...withoutLoading,
            { 
              text: formattedRecommendations,
              sender: 'assistant'
            }
          ];
        }
        
        // If invalid, just return the normal AI response
        return withoutLoading;
      });

    } catch (error) {
      console.error('Product recommendation error:', error);
      setMessages(prev => {
        const withoutLoading = prev.slice(0, -1);
        return [
          ...withoutLoading,
          { text: 'Sorry, I had trouble finding product recommendations. Please try again.', sender: 'assistant' }
        ];
      });
    }
  };

  // Helper function to format product recommendations
  const formatProductRecommendations = (recommendations) => {
    if (!recommendations || !recommendations.product_matches) {
      return null; // Return null instead of error message
    }

    // Return structured data instead of formatted text
    return {
      type: 'product_recommendations',
      data: recommendations
    };
  };

  // Reload conversation history when user auth state changes (e.g., signs in).
  // Debounced 300 ms so rapid isAnonymous flips during auth init don't fire twice.
  useEffect(() => {
    const t = setTimeout(() => loadConversationHistory(), 300);
    return () => clearTimeout(t);
  }, [isAnonymous]);

  // Fetch and format conversation history (returns array or null). Used by loadConversationHistory and by sendMessage error recovery.
  const fetchFormattedHistory = async (currentChatType = 'general', sessionId = null) => {
    const { tokens, userId } = await getAuthSession();
    const accessToken = tokens?.accessToken;
    if (!accessToken || !userId) return null;
    const reqBody = {
      message: '',
      userId: userId,
      getHistory: true,
      chatType: currentChatType,
    };
    if (sessionId) reqBody.sessionId = sessionId;
    const response = await axios.post(getChatRequestUrl(), reqBody, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.data.history || response.data.history.length === 0) return null;
        const historyMessages = await Promise.all(
          response.data.history
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
            .map(async (item) => {
              let imageUrls = item.imageUrls || (item.imageUrl ? [item.imageUrl] : null);
              if (imageUrls && imageUrls.length > 0) {
                try {
                  const refreshedUrls = await Promise.all(
                    imageUrls.map(async (url) => {
                      if (url && url.includes('supabase.co/storage')) {
                        try {
                          const urlMatch = url.match(/\/object\/sign\/images\/(.+?)\?/);
                          if (urlMatch) {
                            const filePath = urlMatch[1];
                            const { data: newSignedData, error: signError } = await supabase.storage
                              .from('images')
                          .createSignedUrl(filePath, 604800);
                        if (!signError && newSignedData?.signedUrl) return newSignedData.signedUrl;
                      }
                    } catch (e) { /* ignore */ }
                  }
                  return url;
                    })
                  );
                  imageUrls = refreshedUrls;
            } catch (e) { /* ignore */ }
                }
              let messageContent = item.message;
              let isStructured = false;
              // Backend may now persist structured product_analysis cards as an object
              // in `text`. Pass those straight through so ChatUI can render the card.
              if (messageContent && typeof messageContent === 'object' && messageContent.type === 'product_analysis') {
                isStructured = true;
              } else {
                if (typeof messageContent === 'string') {
                  messageContent = stripLeadingToolCallJson(messageContent.trim());
                }
                const parsedStructured = tryParseStructured(messageContent);
                if (parsedStructured) {
                  messageContent = parsedStructured;
                  isStructured = true;
                }
              }
              return {
                text: messageContent,
                sender: item.sender || (typeof item.message === 'string' && item.message.includes('Hi!') ? 'assistant' : 'user'),
                imageUrls: imageUrls,
                // Carry the persisted shade cards through reload so swatches survive.
                // #62: the swatch hex is embedded NESTED inside the reply (app.py:8062,
                // the #83 fix), but getHistory only surfaced a top-level shadeMatches —
                // so on reload item.shadeMatches was empty and swatches vanished (the
                // text stayed). Fall back to the nested copy on the parsed reply; hex is
                // permanent, so the swatch re-renders even days later.
                shadeMatches: item.shadeMatches || (messageContent && typeof messageContent === 'object' ? messageContent.shadeMatches : undefined),
                // Carry the persisted skin read through reload so the scan card
                // rehydrates instead of reverting to the prose summary.
                skinAnalysis: item.skinAnalysis || undefined,
                // Rehydrate the rest of the rendered shape the backend now persists,
                // so check-in cards, product-card CTAs and structured layout all come
                // back on reload instead of collapsing to plain text. (The durable
                // fix's third half — anything getHistory returns is spread here.)
                ...(item.skinCheckin ? { skinCheckin: item.skinCheckin } : {}),
                ...(item.productContext ? { productContext: item.productContext } : {}),
                ...(item.productAnalysis ? { productAnalysis: item.productAnalysis } : {}),
                ...(item.productName ? { productName: item.productName } : {}),
                ...(item.productBrand ? { productBrand: item.productBrand } : {}),
                ...(item.productAnalysisProductId ? { productAnalysisProductId: item.productAnalysisProductId } : {}),
                ...(item.productAnalysisProduct ? { productAnalysisProduct: item.productAnalysisProduct } : {}),
                ...(item.imageType ? { imageType: item.imageType } : {}),
                ...(item.journeyAction ? { journeyAction: item.journeyAction } : {}),
                ...(item.journeyContext ? { journeyContext: item.journeyContext } : {}),
                // A persisted skin card that also carries a hero photo IS a scan on
                // reload: map the re-signed hero onto scanImageUrl + flag isSkinScan
                // so ChatUI renders the photo-led "Skin scan" card (its hero reads
                // msg.scanImageUrl), not a bare analysis. The multi-angle thumbnails
                // are live-only base64 (never persisted) — the hero + read survive.
                ...(item.scanImageUrl ? { scanImageUrl: item.scanImageUrl, isSkinScan: true } : {}),
                ...(item.skinAnalysis && imageUrls && imageUrls.length > 0
                  ? { scanImageUrl: imageUrls[0], isSkinScan: true }
                  : {}),
                // Prefer the stored flag; fall back to the parse-derived one.
                isStructured: item.isStructured || isStructured
              };
            })
        );
    return historyMessages;
  };

  // #47: recover a server-saved answer when the connection dropped (phone slept / tab
  // backgrounded) mid-request. The backend generates + persists the reply to the
  // conversation regardless of whether the client stayed connected, so on failure/resume
  // we re-fetch history and, if our just-sent turn now has an assistant reply, render it —
  // WITHOUT re-sending (a re-send would double-generate and double-charge).
  const recoverAnswerFromHistory = async ({ retries = 6, delayMs = 2500 } = {}) => {
    const sent = (messageBeingSentRef.current || '').trim();
    if (!sent) return false;
    if (turnSettledRef.current) return false; // #47: this turn already rendered its answer — never redraw/clobber (kills the flicker + product-swap)
    // POLL history a few times. The backend keeps generating + saving the reply even
    // after our connection dropped, so give it time to land BEFORE any re-send. Racing
    // straight to a re-send created a SECOND server turn — the "same question answered
    // twice with two different answers" bug Sofia hit (#47). ~15s window covers a normal
    // generation; only if nothing ever lands do we fall through to the re-send.
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const history = await fetchFormattedHistory(chatType, currentSessionIdRef.current);
        if (Array.isArray(history) && history.length >= 2) {
          let ourIdx = -1;
          for (let i = history.length - 1; i >= 0; i--) {
            const m = history[i];
            const t = typeof m?.text === 'string' ? m.text : '';
            if (m?.sender === 'user' && (t.trim() === sent || t.includes(sent))) { ourIdx = i; break; }
          }
          if (ourIdx >= 0 && history[ourIdx + 1]?.sender === 'assistant') {
            setMessages(history);
            return true;
          }
        }
      } catch (_) { /* keep polling */ }
      if (attempt < retries - 1) await new Promise((r) => setTimeout(r, delayMs));
    }
    return false;
  };

  // #47: if the app returns to the foreground while a send is still pending, the stream
  // likely died in the background — try to recover the saved answer instead of spinning.
  useEffect(() => {
    const onResume = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (!sendingRef.current) return;
      if (await recoverAnswerFromHistory()) {
        turnSettledRef.current = true;
        activeRequestAbortRef.current?.();
        sendingRef.current = false;
        setIsLoading(false);
        setStreamStatus('');
      }
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('focus', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, [chatType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Modified loadConversationHistory to include chat type and sessionId
  const loadConversationHistory = async (currentChatType = 'general', sessionId = null, _attempt = 1) => {
    try {
      const { tokens, userId } = await getAuthSession();
      const accessToken = tokens?.accessToken;

      if (!accessToken || !userId) {
        // Auth hydration is async — on a fresh load the session often isn't
        // ready yet at the first attempt. Retry a few times before giving up,
        // otherwise a logged-in user sees an empty chat (no history).
        if (_attempt < 6) {
          setTimeout(
            () => loadConversationHistory(currentChatType, sessionId, _attempt + 1),
            500,
          );
          return;
        }
        console.error('❌ No auth after retries - skipping history load');
        setIsLoadingHistory(false);
        return;
      }

      // Initialise session ID from localStorage if not already set
      let effectiveSessionId = sessionId || currentSessionIdRef.current;
      if (!effectiveSessionId) {
        try {
          const storedChats = localStorage.getItem(`sonmi_chats_${userId}`);
          if (storedChats) setChatList(JSON.parse(storedChats));
          const storedSession = localStorage.getItem(`sonmi_current_session_${userId}`);
          if (storedSession) {
            effectiveSessionId = storedSession;
          } else {
            effectiveSessionId = crypto.randomUUID();
            localStorage.setItem(`sonmi_current_session_${userId}`, effectiveSessionId);
          }
          currentSessionIdRef.current = effectiveSessionId;
          setCurrentSessionId(effectiveSessionId);
        } catch (e) { /* ignore storage errors */ }
      }

      const historyMessages = await fetchFormattedHistory(currentChatType, effectiveSessionId);
      if (historyMessages && historyMessages.length > 0) {
        setMessages(historyMessages);
        const latestJourney = [...historyMessages].reverse().find((item) => item.journeyContext)?.journeyContext;
        // A conversation without journey metadata must clear the previous
        // conversation's image reference. Otherwise a later photo can be sent
        // beside an unrelated stale attachment.
        setActiveJourney(latestJourney || null);
      } else {
        setMessages([WELCOME_MESSAGE_OBJ]);
        setActiveJourney(null);
      }
      // Removed setLatestAnalysis call as component was removed
    } catch (error) {
      const isNetworkError = error?.code === 'ERR_NETWORK_CHANGED' ||
        error?.message?.includes('Network Error') ||
        error?.message?.includes('ERR_NETWORK');
      if (isNetworkError && _attempt < 3) {
        const delay = _attempt * 1500;
        setTimeout(() => loadConversationHistory(currentChatType, sessionId, _attempt + 1), delay);
        return;
      }
      console.error('Error loading conversation history:', error);
      setMessages([WELCOME_MESSAGE_OBJ]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Start a fresh conversation with a new sessionId
  const handleNewChat = () => {
    const newSessionId = crypto.randomUUID();
    currentSessionIdRef.current = newSessionId;
    setCurrentSessionId(newSessionId);
    if (currentUserId) {
      localStorage.setItem(`sonmi_current_session_${currentUserId}`, newSessionId);
    }
    // Reset only conversation state — profile carries over
    setMessages([WELCOME_MESSAGE_OBJ]);
    setSuggestionsDismissed(false);
    setUploadedImages([]);
    setShowAnalyzedPhotos(false);
    setActiveJourney(null);
  };

  // A Colour Analysis handoff starts a fresh guided journey rather than
  // reopening an unrelated chat. Wait for auth/history hydration first.
  useEffect(() => {
    if (isLoadingHistory || colourJourneyEntryHandled.current) return;
    if (new URLSearchParams(window.location.search).get('journey') !== 'colour-makeup') return;
    colourJourneyEntryHandled.current = true;
    handleNewChat();
  }, [isLoadingHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch to a different existing conversation
  const handleSwitchChat = async (sessionId) => {
    if (sessionId === currentSessionIdRef.current) return;
    currentSessionIdRef.current = sessionId;
    setCurrentSessionId(sessionId);
    restoreActiveJourney(sessionId);
    if (currentUserId) {
      localStorage.setItem(`sonmi_current_session_${currentUserId}`, sessionId);
    }
    // Show a loading indicator inline (no full-page spinner)
    setMessages([{ text: 'Loading conversation…', sender: 'assistant', isLoading: true }]);
    try {
      const historyMessages = await fetchFormattedHistory(chatType, sessionId);
      if (historyMessages && historyMessages.length > 0) {
        setMessages(historyMessages);
      } else {
        setMessages([WELCOME_MESSAGE_OBJ]);
      }
    } catch (e) {
      setMessages([WELCOME_MESSAGE_OBJ]);
    }
  };

  // Modified sendMessage to include chat type
  // Handle upgrade request - simple Supabase logging
  const requestUpgrade = async () => {
    try {
      const { tokens, userId } = await getAuthSession();
      if (!userId) {
        throw new Error('Authentication required');
      }

      // Log upgrade request directly to Supabase
      const { error } = await supabase
        .from('upgrade_requests')
        .insert({
          user_id: userId,
          requested_at: new Date().toISOString(),
          status: 'pending',
          source: 'chat_limit'
        });

      if (error) throw error;

      // Show confirmation message
      const confirmMessage = { 
        text: "We've logged your interest. This feature will be available soon - we'll let you know! 🎯", 
        sender: 'assistant'
      };
      setMessages(prev => [...prev, confirmMessage]);

    } catch (error) {
      console.error('Error requesting upgrade:', error);
      const errorMessage = { 
        text: 'We\'ve logged your interest. This feature will be available soon - we\'ll let you know! 🎯', 
        sender: 'assistant' 
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  // The quota gate answers with {paywall:true} and NO reply. Every response path
  // must check this before it looks for `reply`, otherwise the turn falls through
  // to "Missing reply" and shows a generic error instead of the paywall.
  // Returns true when it handled the turn.
  const handlePaywallResponse = (data) => {
    if (!data || !data.paywall) return false;
    setPaywallBusy(false);   // never reopen stuck in the busy state
    // The question that hit the wall is otherwise lost: checkout navigates away
    // from the site, so React state does not survive. Stash it and re-send it
    // once they come back subscribed, rather than making them retype.
    try {
      const pending = (messageBeingSentRef.current || '').trim();
      if (pending) localStorage.setItem('sonmi_pending_question', pending);
    } catch (_e) {}
    setShowPaywall(true);
    setIsLoading(false);
    setStreamStatus('');
    turnSettledRef.current = true;
    sendingRef.current = false;
    return true;
  };

  // Hand off to Stripe Checkout. The backend creates the session against the
  // signed-in user, so the entitlement can be attached when the webhook lands;
  // we only follow the URL it returns.
  useEffect(() => {
    const clear = () => setPaywallBusy(false);
    window.addEventListener('pageshow', clear);
    return () => window.removeEventListener('pageshow', clear);
  }, []);

  // Coming back from Stripe. There is no confirmation step otherwise: Stripe
  // redirects straight into the chat, so without this the purchase is silent and
  // the question that triggered the paywall has vanished.
  useEffect(() => {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (_e) { return; }
    if (params.get('checkout') !== 'success') return;

    // Clear the query string so a refresh doesn't replay this.
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch (_e) {}

    setShowPaywall(false);
    setPaywallBusy(false);
    setMessages(prev => ([...prev, {
      text: "You're in, and thank you. Son:mi+ is active on your account: unlimited skin scans and your progress journal, personalised routines and product checks, and cross-brand shade matching. Everything stays saved to your beauty profile.",
      sender: 'assistant',
      timestamp: new Date().toISOString(),
    }]));

    let pending = null;
    try {
      pending = localStorage.getItem('sonmi_pending_question');
      localStorage.removeItem('sonmi_pending_question');
    } catch (_e) {}
    if (pending && pending.trim()) {
      // Give the confirmation a beat to land before their answer starts.
      setTimeout(() => sendMessage(pending), 900);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCheckout = async (plan) => {
    try {
      setPaywallBusy(true);
      const { tokens } = await getAuthSession();
      const accessToken = tokens?.accessToken;
      if (!accessToken) {
        setPaywallBusy(false);
        setShowPaywall(false);
        setShowUpgradeModal(true);
        return;
      }
      const res = await axios.post(getBillingUrl('/checkout'), { plan }, {
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        timeout: 20000,
      });
      if (res.data && res.data.url) {
        window.location.assign(res.data.url);
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (e) {
      console.error('Checkout could not start:', e);
      setPaywallBusy(false);
      const foundingFull = e?.response?.status === 409 && e?.response?.data?.error === 'founding_full';
      setMessages(prev => ([...prev, {
        text: foundingFull
          ? "Our founding memberships are all taken. You can still join Son:mi+ on the standard annual plan."
          : "I couldn't open checkout just then. Please try again in a moment.",
        sender: 'assistant',
      }]));
      setShowPaywall(false);
    }
  };

  const sendMessage = async (messageOverride) => {
    // The hard registration gate applies to every route that can create another
    // answer, including CTA-driven and guided turns which do not use the composer.
    if (reopenGate()) return;
    // messageOverride lets CTA buttons (e.g. "See shade matches") send a
    // canned message without relying on React state propagation from setInput.
    // Without this, clicking a CTA right after setInput("...") sometimes read
    // stale state and sent nothing — leaving the user with an empty input,
    // no visible user bubble, and no clue what they "asked".
    const messageCommand = messageOverride && typeof messageOverride === 'object'
      ? messageOverride
      : null;
    const messageText = messageCommand?.prompt?.trim()
      ? messageCommand.prompt
      : (typeof messageOverride === 'string' && messageOverride.trim())
        ? messageOverride
        : input;
    const displayMessageText = messageCommand?.displayText?.trim() || messageText;
    if (!messageText.trim()) return;
    if (sendingRef.current) return; // prevent double-send
    sendingRef.current = true;
    turnSettledRef.current = false; // #47: new turn — not yet settled

    messageBeingSentRef.current = messageText.trim();
    const productAnalysisContext = getProductAnalysisContext(messages, messageBeingSentRef.current);
    const userMessage = { text: displayMessageText, sender: 'user',
      ...(productAnalysisContext?.productId ? { productAnalysisProductId: productAnalysisContext.productId } : {}),
      ...(productAnalysisContext?.product ? { productAnalysisProduct: productAnalysisContext.product } : {}) };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setInput('');

    // Track chat message sent
    trackChatMessageSent(false);

    try {
      const { tokens, userId } = await getAuthSession();
      const accessToken = tokens?.accessToken;
      if (!accessToken || !userId) {
        throw new Error('Authentication required');
      }

      const buildBody = () => {
        const b = { message: messageBeingSentRef.current, userId, chatType };
        if (displayMessageText !== messageBeingSentRef.current) b.displayMessage = displayMessageText;
        if (productAnalysisContext) {
          b.productAnalysisExcerpt = productAnalysisContext.excerpt;
          b.productAnalysisProduct = productAnalysisContext.product;
          if (productAnalysisContext.productId) b.productAnalysisProductId = productAnalysisContext.productId;
        }
        if (currentSessionIdRef.current) b.sessionId = currentSessionIdRef.current;
        // The ref is the fast path. The latest rendered journey context is a
        // safe fallback after session/history transitions, so a focused-area
        // turn cannot silently become a generic full-profile conversation.
        const journey = activeJourneyRef.current
          || [...messages].reverse().find((item) => item?.journeyContext)?.journeyContext;
        if (journey?.imageRole) b.image_role = journey.imageRole;
        if (journey?.analysisScope) b.analysis_scope = journey.analysisScope;
        if (journey?.productTitle) b.journey_product = journey.productTitle;
        if (journey?.catalogProductId) b.catalog_product_id = journey.catalogProductId;
        if (journey?.referenceImageUrl) b.journey_reference_url = journey.referenceImageUrl;
        if (journey?.referenceKind) b.journey_reference_kind = journey.referenceKind;
        return b;
      };

      // ── Function URL path: single request/response (no 29s API Gateway cap). On localhost, getChatRequestUrl() is the Function URL. ──
      const colourChat = process.env.REACT_APP_ENABLE_COLOUR_ANALYSIS === 'true'
        && typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('from') === 'colour-analysis';
      const chatRequestUrl = colourChat ? getColourAnalysisRequestUrl() : getChatRequestUrl();
      const streamUrl = colourChat ? chatRequestUrl : (config.STREAM_URL || chatRequestUrl);
      if (streamUrl) {
        setStreamStatus('This may take a minute…');
        typewriterCleanupRef.current?.();

        let requestTimeout = null;
        try {
          const body = JSON.stringify(buildBody());
          requestTimeout = createRequestTimeout({
            onSlow: () => setStreamStatus('Still working…'),
          });
          activeRequestAbortRef.current = requestTimeout.abort;
          const res = await fetch(streamUrl, {
            method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
            body,
            signal: requestTimeout.signal,
          });
          const rawText = await res.text();
          requestTimeout.clear();
          activeRequestAbortRef.current = null;

          if (!res.ok) {
            console.error('[Chat] Stream URL error:', res.status, rawText?.slice(0, 400));
            throw new Error(rawText || `Request error: ${res.status}`);
          }

          let data = {};
          try {
            data = rawText ? JSON.parse(rawText) : {};
          } catch (parseErr) {
            console.error('[Chat] Stream URL invalid JSON:', rawText?.slice(0, 400));
            throw parseErr;
          }
          if (data.journey_context) {
            setActiveJourney({
              ...(activeJourneyRef.current || {}),
              ...data.journey_context,
            });
          }
          if (handlePaywallResponse(data)) return;
          if (data.reply === undefined || data.reply === null) {
            if (data.error) console.error('[Chat] Backend error:', data.error);
            throw new Error(data.error || 'Missing reply');
          }

          let messageContent = data.reply;
          turnSettledRef.current = true; // #47: answer in hand — block any recovery redraw/swap
          if (typeof messageContent === 'string') {
            messageContent = stripLeadingToolCallJson(messageContent.trim());
          }
          let isStructured = false;
          if (typeof messageContent === 'object' && messageContent !== null) {
            isStructured = true;
          } else if (typeof messageContent === 'string') {
            const parsed = tryParseStructured(messageContent);
            if (parsed) { messageContent = parsed; isStructured = true; }
          }
          const extraFields = {
            searchUsed: data.searchUsed,
            productAnalysis: data.productAnalysis,
            skinAnalysis: data.skinAnalysis,
            skinCheckin: data.skinCheckin || null,
            imageType: data.imageType,
            productName: data.productName,
            productBrand: data.productBrand,
            shadeMatches: data.shadeMatches,
            journeyAction: data.journeyAction || messageCommand?.journeyAction,
            journeyContext: data.journey_context || messageCommand?.journeyContext,
          };
          if (isStructured && messageContent?.advice) {
            // follow_up is part of messageContent and rendered inline by
            // StructuredMessage (persists on reload) — no separate bubble.
            const cleanup = typewriterAnimate(
              { ...messageContent, ...extraFields },
              setMessages,
              () => {
                typewriterCleanupRef.current = null;
                // 2026-06-12: follow_up is now rendered INLINE by StructuredMessage
                // (from content.follow_up) so it persists on reload. No separate bubble.
                if (isAnonymous) {
                  totalAnonMessages.current += 1;
                  noteAnonInteraction();
                }
              }
            );
            typewriterCleanupRef.current = cleanup;
          } else {
            setMessages(prev => [...prev, { text: messageContent, sender: 'assistant', isStructured, ...extraFields }]);
            // follow_up rendered inline by StructuredMessage (persists on reload).
            if (isAnonymous) {
              totalAnonMessages.current += 1;
              noteAnonInteraction();
            }
          }
          sendingRef.current = false;
          setIsLoading(false);
          setStreamStatus('');
          return;
        } catch (err) {
          const requestTimedOut = requestTimeout?.didTimeout() || false;
          requestTimeout?.clear();
          activeRequestAbortRef.current = null;
          console.error('[Chat] Stream URL request failed:', err?.message || err, err);
          reportError(err, 'chat_stream_request', { timed_out: requestTimedOut });
          // The resume handler may already have restored this answer from history
          // and aborted the stale browser connection. Never redraw or resend it.
          if (turnSettledRef.current) {
            sendingRef.current = false;
            setIsLoading(false);
            setStreamStatus('');
            return;
          }
          const isAuth = err?.message?.includes('401') || (err?.message && err.message.length < 200 && err.message.includes('auth'));
          if (isAuth) {
            setMessages(prev => [...prev, { text: 'Session expired. Please refresh the page and try again.', sender: 'assistant' }]);
            sendingRef.current = false;
            setIsLoading(false);
            setStreamStatus('');
            return;
          }
          // #47: the connection may have dropped (phone slept / tab backgrounded) AFTER
          // the server already generated + saved the reply. Recover it from history before
          // re-sending — re-sending would double-generate and double-charge.
          if (await recoverAnswerFromHistory()) {
            turnSettledRef.current = true;
            sendingRef.current = false;
            setIsLoading(false);
            setStreamStatus('');
            return;
          }
          // A timed-out connection may still represent a request the server is
          // completing. An automatic fallback would submit the same turn twice.
          if (requestTimedOut) {
            const failedJourney = activeJourneyRef.current?.imageRole;
            if (failedJourney) trackOutcomeJourneyFailed(failedJourney, 'chat_request_timeout');
            setMessages(prev => [...prev, {
              text: "I couldn't retrieve that reply. Your message is still in the conversation, so please try again when you're ready.",
              sender: 'assistant',
            }]);
            sendingRef.current = false;
            setIsLoading(false);
            setStreamStatus('');
            return;
          }
          // Fallback: try API (may hit 29s timeout but often works for follow-ups)
          try {
            setStreamStatus('Retrying…');
            const apiRes = await axios.post(chatRequestUrl, buildBody(), {
              headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
              timeout: 90000, // see note on the primary POST above
            });
            const data = apiRes.data;
            if (data.journey_context) {
              setActiveJourney({
                ...(activeJourneyRef.current || {}),
                ...data.journey_context,
              });
            }
            if (handlePaywallResponse(data)) return;
            let messageContent = data.reply;
            turnSettledRef.current = true; // #47: answer in hand — block any recovery redraw/swap
            if (typeof messageContent === 'string') {
              messageContent = stripLeadingToolCallJson(messageContent.trim());
            }
            let isStructured = false;
            if (typeof messageContent === 'object' && messageContent !== null) {
              isStructured = true;
            } else if (typeof messageContent === 'string') {
              const parsed = tryParseStructured(messageContent);
              if (parsed) { messageContent = parsed; isStructured = true; }
            }
            const extraFields = {
              searchUsed: data.searchUsed,
              productAnalysis: data.productAnalysis,
              skinAnalysis: data.skinAnalysis,
              skinCheckin: data.skinCheckin || null,
              imageType: data.imageType,
              productName: data.productName,
              productBrand: data.productBrand,
              shadeMatches: data.shadeMatches,
              journeyAction: data.journeyAction || messageCommand?.journeyAction,
              journeyContext: data.journey_context || messageCommand?.journeyContext,
            };
            if (isStructured && messageContent?.advice) {
              const cleanup = typewriterAnimate(
                { ...messageContent, ...extraFields },
                setMessages,
                () => {
                  typewriterCleanupRef.current = null;
                  // follow_up rendered inline by StructuredMessage (persists on reload).
                }
              );
              typewriterCleanupRef.current = cleanup;
            } else {
              setMessages(prev => [...prev, { text: messageContent, sender: 'assistant', isStructured, ...extraFields }]);
              // follow_up rendered inline by StructuredMessage (persists on reload).
            }
          } catch (fallbackErr) {
            console.error('[Chat] API fallback also failed:', fallbackErr?.message || fallbackErr);
            reportError(fallbackErr, 'chat_fallback_request', {
              status: fallbackErr?.response?.status || 0,
            });
            const failedJourney = activeJourneyRef.current?.imageRole;
            if (failedJourney) trackOutcomeJourneyFailed(failedJourney, 'chat_request_failed');
            setMessages(prev => [...prev, { text: 'Sorry, I encountered an error. Please try again.', sender: 'assistant' }]);
          }
        }
        sendingRef.current = false;
        setIsLoading(false);
        setStreamStatus('');
        return;
      }

      // ── Non-streaming (axios) path ────────────────────────────────────
      const doPost = () => axios.post(chatRequestUrl, buildBody(), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        // 2026-07: raised 35s -> 90s. Requests now go to the Lambda Function URL
        // (no 29s API Gateway cap), and a cold multi-hop tool loop genuinely
        // takes longer than 35s -- a dev shade query logged 55.3s with
        // hops=4 (catalog_search x2, gather_shades, visual_shade_match) and
        // returned 3 shades + 2 cards. The client was aborting ~20s before the
        // answer arrived, so a WORKING backend rendered as "no shades". Warm
        // prod lambdas usually come in under 35s, which is why this hid there.
        // The 55s itself is the real bug; this stops it presenting as breakage.
        timeout: 90000,
      });
      let response;
      try {
        response = await doPost();
      } catch (firstErr) {
        const status = firstErr.response?.status;
        if ((status === 503 || status === 504) && !firstErr.config?.__retried) {
          await new Promise(r => setTimeout(r, 1500));
          response = await doPost();
        } else {
          throw firstErr;
        }
      }

      if (handlePaywallResponse(response.data)) return;
      const reply = response.data?.reply;
      if (reply === undefined || reply === null) {
        throw new Error('Missing reply');
      }
      let messageContent = reply;
      let isStructured = false;

      if (typeof reply === 'object' && reply !== null) {
        messageContent = reply;
        isStructured = true;
      } else if (typeof reply === 'string') {
        const stripped = stripLeadingToolCallJson(reply.trim());
        const parsed = tryParseStructured(stripped);
        if (parsed) { messageContent = parsed; isStructured = true; }
        else messageContent = stripped;
      }
      
      const assistantMessage = { 
        text: messageContent, 
        sender: 'assistant',
        searchUsed: response.data.searchUsed,
        showUpgradePrompt: response.data.showUpgradePrompt,
        isStructured,
        productAnalysis: response.data.productAnalysis,
        skinAnalysis: response.data.skinAnalysis,
        skinCheckin: response.data.skinCheckin || null,
        imageType: response.data.imageType,
        productName: response.data.productName,
        productBrand: response.data.productBrand,
      };
      setMessages(prev => [...prev, assistantMessage]);
      // follow_up rendered inline by StructuredMessage (persists on reload).

      if (isAnonymous) {
        totalAnonMessages.current += 1;
        noteAnonInteraction();
      }

    } catch (error) {
      console.error('Error sending message:', error);
      reportError(error, 'chat_request', {
        status: error?.response?.status || 0,
        timed_out: error?.code === 'ECONNABORTED',
      });

      // Stop any in-flight typewriter animation — otherwise it keeps mutating
      // the last message (potentially the recovery history we're about to load),
      // and leaves its partial bubble alongside the full response from history.
      try { typewriterCleanupRef.current?.(); } catch (_) {}
      typewriterCleanupRef.current = null;

      // Remove both the explicit streaming placeholder AND any message still flagged
      // _streaming (partial typewriter output) so the recovery path doesn't leave a
      // duplicate, incomplete bubble next to the full message loaded from history.
      setMessages(prev => prev.filter(m => !m.isStreaming && !m._streaming));

      let errorText = 'Sorry, I encountered an error. Please try again.';
      if (error.response?.status === 401) {
        errorText = 'Authentication error. Please refresh the page and try again.';
      } else if (error.response?.status === 403) {
        errorText = 'Access denied. Please check your permissions.';
      } else if (error.response?.status >= 500) {
        errorText = 'Server error. Please try again in a moment.';
      } else if (error.message?.includes('Network Error')) {
        errorText = 'Network connection issue. Please check your internet and try again.';
      }
      
      // Backend may still be writing; poll history with longer delays (request can take 30–60s)
      const delays = [3000, 8000, 15000, 25000]; // poll at 3s, 11s, 26s, 51s
      setMessages(prev => {
        recoveryMessageCountRef.current = prev.length;
        return [...prev, { text: 'Checking for your reply…', sender: 'assistant', _checking: true }];
      });
      try {
        let history = null;
        for (const delay of delays) {
          await new Promise(r => setTimeout(r, delay));
          try {
            history = await fetchFormattedHistory(chatType, currentSessionIdRef.current);
          } catch (_) {
            continue;
          }
          const minCount = recoveryMessageCountRef.current;
          if (history && history.length > minCount && history[history.length - 1].sender === 'assistant') {
            setMessages(history);
            return;
          }
        }
        const failedJourney = activeJourneyRef.current?.imageRole;
        if (failedJourney) trackOutcomeJourneyFailed(failedJourney, 'chat_request_failed');
        setMessages(prev => [...prev.filter(m => !m._checking), { text: errorText, sender: 'assistant' }]);
      } catch (_) {
        const failedJourney = activeJourneyRef.current?.imageRole;
        if (failedJourney) trackOutcomeJourneyFailed(failedJourney, 'chat_request_failed');
        setMessages(prev => [...prev.filter(m => !m._checking), { text: errorText, sender: 'assistant' }]);
      }
    } finally {
      sendingRef.current = false;
      setIsLoading(false);
      setStreamStatus('');
    }
  };

  // Handle refine results from feedback
  const handleRefineResults = async (refineFilters) => {
    
    // Create a refined search message based on filters
    let refineMessage = "Please refine my previous recommendations";
    
    if (refineFilters.budget_concern) {
      refineMessage += " with more budget-friendly options";
    }
    if (refineFilters.shade_issue) {
      refineMessage += " with better shade matching for my undertones";
    }
    if (refineFilters.location_issue) {
      refineMessage += " that are available in my location";
    }
    if (refineFilters.texture_sensitivity) {
      refineMessage += " with different textures or hypoallergenic options";
    }
    
    // Add the refine message and send it
    setInput(refineMessage);
    
    // Trigger send message after a brief delay to allow input to be set
    setTimeout(() => {
      const syntheticEvent = { preventDefault: () => {} };
      sendMessage();
    }, 100);
  };

  // Enhanced multiple image upload with contextual analysis
  const uploadImages = async (files, context = '') => {
    if (reopenGate()) return;
    try {
      
      // Process all images first
      const imagePromises = Array.from(files).map(file => processImageFile(file));
      const processedImages = await Promise.all(imagePromises);
      
      // Upload all images to Supabase and get signed URLs
      const uploadPromises = processedImages.map(async (img, index) => {
        // Use index + random to ensure unique file paths
        const fileName = img.originalFileName || `image_${index}`;
        const filePath = `uploaded_images/${img.userId}/${Date.now()}_${index}_${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('images')
          .upload(filePath, img.file, {
            cacheControl: '3600',
            upsert: false,
            contentType: img.file.type,
          });
        if (uploadError) throw uploadError;

        // Generate a signed URL
        const { data, error: signError } = await supabase.storage
          .from('images')
          .createSignedUrl(filePath, 604800); // 7 days
        if (signError) throw signError;
        
        return {
          ...img,
          signedUrl: data.signedUrl,
          filePath: filePath
        };
      });
      
      const uploadedImages = await Promise.all(uploadPromises);
      
      // Send multiple images directly to backend for analysis (no type selection needed)
      await sendMultipleImagesForAnalysis(uploadedImages, context, 'auto');

      // Add images to uploaded images list
      uploadedImages.forEach(img => {
        setUploadedImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          url: img.signedUrl,
          name: img.file.name,
          timestamp: new Date().toISOString()
        }]);
      });

      // Clear input after successful upload
      setInput('');
      
    } catch (error) {
      console.error('Error in batch image upload:', error);
      // Fallback to individual uploads
      const uploadPromises = Array.from(files).map(file => uploadSingleImage(file));
      return Promise.all(uploadPromises);
    }
  };

  // Cleanup preview URLs to prevent memory leaks
  const cleanupPreviewUrls = () => {
    previewUrls.current.forEach(url => {
      URL.revokeObjectURL(url); // Free memory
    });
    previewUrls.current = [];
  };

  // Show analysis type choice modal for multiple images
  const showAnalysisTypeChoice = (files, prompt = '') => {
    // Clean up any previous URLs first
    cleanupPreviewUrls();
    
    // Store files for upload (keep original File objects)
    setPendingFiles(files);
    setPendingPrompt(prompt);
    
    // Create preview URLs (temporary, for modal display only)
    previewUrls.current = files.map(file => URL.createObjectURL(file));
    
    setShowAnalysisChoice(true);
  };

  // Handle analysis type selection
  const handleAnalysisTypeSelect = async (analysisType) => {
    setShowAnalysisChoice(false);
    
    // PRODUCT ANALYSIS MOVED TO PRODUCT CATALOG
    // Only skin analysis is handled in chat now
    if (analysisType === 'skin') {
      // Skin analysis: batch upload (efficient, one comprehensive analysis)
      await uploadImages(pendingFiles, pendingPrompt);
    } 
    // REMOVED: Product analysis - now handled in Product Catalog module
    // else if (analysisType === 'product') {
    //   console.log(`User selected PRODUCT analysis for ${pendingFiles.length} images`);
    //   await uploadImagesSequentially(pendingFiles, pendingPrompt);
    // }
    
    // Cleanup: Free preview URLs (modal closed, no longer needed)
    cleanupPreviewUrls();
    
    // Clear pending files
    setPendingFiles([]);
    setPendingPrompt('');
  };

  // Cancel analysis type selection
  const handleAnalysisTypeCancel = () => {
    setShowAnalysisChoice(false);
    
    // Cleanup: Free preview URLs
    cleanupPreviewUrls();
    
    setPendingFiles([]);
    setPendingPrompt('');
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupPreviewUrls(); // Component unmounting, free all resources
    };
  }, []);

  // REMOVED: Sequential upload for product analysis
  // Product analysis moved to Product Catalog module
  // const uploadImagesSequentially = async (files, context = '') => {
  //   try {
  //     console.log(`Starting sequential upload of ${files.length} images...`);
  //     
  //     setMessages(prev => [...prev, {
  //       text: `✨ Analyzing ${files.length} product${files.length > 1 ? 's' : ''} separately for detailed individual insights...`,
  //       sender: 'assistant',
  //       timestamp: new Date().toISOString()
  //     }]);
  //     
  //     for (let i = 0; i < files.length; i++) {
  //       const file = files[i];
  //       console.log(`Uploading image ${i + 1}/${files.length}: ${file.name}`);
  //       
  //       try {
  //         await uploadSingleImage(file, true);
  //         
  //         if (i < files.length - 1) {
  //           await new Promise(resolve => setTimeout(resolve, 1000));
  //         }
  //       } catch (error) {
  //         console.error(`Error uploading image ${i + 1}:`, error);
  //         setMessages(prev => [...prev, {
  //           text: `❌ Failed to analyze ${file.name}. Continuing with remaining images...`,
  //           sender: 'assistant',
  //           timestamp: new Date().toISOString()
  //         }]);
  //       }
  //     }
  //     
  //     console.log(`✅ Completed sequential upload of ${files.length} images`);
  //   } catch (error) {
  //     console.error('Error in sequential upload:', error);
  //     setMessages(prev => [...prev, {
  //       text: '❌ An error occurred while processing your images. Please try uploading them one at a time.',
  //       sender: 'assistant',
  //       timestamp: new Date().toISOString()
  //     }]);
  //   }
  // };

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Helper function to process image file (resize, convert to base64)
  const processImageFile = async (file) => {
    // Validate file first
    validateImageFile(file);

    // Get session from Supabase useAuth
    const { tokens, userId } = await getAuthSession();
    const accessToken = tokens?.accessToken;

    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Resize image if needed
    const resizedFile = await resizeAndOptimizeImage(file);

    // Convert to base64
    const base64Data = await fileToBase64(resizedFile);

    return {
      file: resizedFile,
      base64Data: base64Data,
      userId: userId,
      accessToken: accessToken,
      originalFileName: file.name  // Preserve original file name
    };
  };

  // Send single image for analysis
  const sendImageForAnalysis = async (imageFile, signedUrl, userMessage = '', skipLoadingMessage = false, opts = {}) => {
    let requestTimeout = null;
    try {
      const { tokens, userId } = await getAuthSession();
      const accessToken = tokens?.accessToken;

      if (!accessToken || !userId) {
        console.error('❌ Auth check failed:', { hasTokens: !!tokens, hasAccessToken: !!accessToken, hasUserId: !!userId });
        throw new Error('Authentication required. Please refresh the page and sign in again.');
      }

      // Show bottom loading indicator (with TypingFact) during image analysis
      setIsLoading(true);
      setStreamStatus('');
      requestTimeout = createRequestTimeout({
        onSlow: () => setStreamStatus('Still working…'),
      });

      // Convert the optimized image file to base64
      const imageBase64 = await fileToBase64(imageFile);

      // Capture a prior inspiration before replacing the active role. A normal
      // chat photo can then answer "why does her look work and mine doesn't?"
      // using both images, while still remaining a flexible chat upload.
      const priorReferenceImageUrl = opts.referenceImageUrl || activeJourneyRef.current?.referenceImageUrl;
      const priorReferenceKind = opts.referenceKind || activeJourneyRef.current?.referenceKind;
      const referenceImageUrl = opts.imageRole === IMAGE_ROLES.GENERIC_CHAT_PHOTO && priorReferenceKind !== 'inspiration'
        ? null
        : priorReferenceImageUrl;
      const referenceKind = referenceImageUrl ? priorReferenceKind : null;

      const journeyContext = opts.imageRole ? {
        imageRole: opts.imageRole,
        ...(opts.analysisScope ? { analysisScope: opts.analysisScope } : {}),
        ...(opts.productTitle ? { productTitle: opts.productTitle } : {}),
        ...(opts.catalogProductId ? { catalogProductId: opts.catalogProductId } : {}),
        ...(opts.searchedProduct ? { searchedProduct: opts.searchedProduct } : {}),
        ...(opts.referenceImageUrl ? { referenceImageUrl: opts.referenceImageUrl } : {}),
        ...(opts.referenceKind ? { referenceKind: opts.referenceKind } : {}),
        ...((opts.imageRole === IMAGE_ROLES.INSPIRATION_REFERENCE) ? { referenceKind: 'inspiration' } : {}),
        ...((opts.imageRole === IMAGE_ROLES.INSPIRATION_REFERENCE && signedUrl) ? { referenceImageUrl: signedUrl } : {}),
        ...((opts.imageRole === IMAGE_ROLES.PRODUCT_REFERENCE && signedUrl) ? { referenceImageUrl: signedUrl } : {}),
      } : null;
      if (journeyContext) setActiveJourney(journeyContext);

      // Guided scan: also send the OTHER captured angles (both cheeks, chin-down)
      // so the backend reads the FULL set, not just the straight-on. The front
      // stays the card hero (image_data); every angle goes in `images` so the
      // multi-angle read fires (backend analyses all of them together).
      let scanImages = null;
      if (opts.guidedScan && Array.isArray(opts.scanAngles) && opts.scanAngles.length > 1) {
        try {
          // Resize EACH angle before base64 — the SAME path the front already
          // uses (image_data = fileToBase64(resizeAndOptimizeImage(file))). The
          // raw guided-capture stills are full camera resolution; sending 4 of
          // them un-resized blew the Lambda Function URL's 6MB request cap → a
          // 413 BEFORE the handler ran (nothing in the lambda logs; the frontend
          // catch surfaced it as "Image upload failed"). Capping each at
          // 1200px / JPEG-0.8 (~200-400KB) keeps the whole multi-angle body
          // well under 6MB — and cuts vision cost + latency as a bonus.
          scanImages = await Promise.all(
            opts.scanAngles.map(async (f) => fileToBase64(await resizeAndOptimizeImage(f)))
          );
        } catch (_e) {
          scanImages = null;  // fall back to single-image analysis on any hiccup
        }
      }

      // Send image to conversation endpoint with auto-detection
      const message = userMessage ?
        `${userMessage}\n\nI've uploaded an image. Please analyze what you see and provide helpful insights based on the image content.` :
        `I've uploaded an image. Please analyze what you see and provide helpful insights based on the image content.`;

      // Push the upload as a user bubble BEFORE the backend call so the photo
      // shows up in the chat alongside the user's typed text (if any). The
      // canned "I've uploaded an image…" prompt suffix gets stripped on render
      // via stripImageBoilerplate, so what the user sees is just their words
      // (or nothing) + the photo.
      //
      // EXCEPTION: the guided skin scan does NOT push a raw selfie bubble — the
      // captured photo becomes the hero of the scan-result card instead, so the
      // result reads as a produced "Skin scan", not a picture the user dropped
      // in and got text back on. (The guided-capture UI already showed the shot.)
      if (!opts.guidedScan) {
        setMessages(prev => ([
          ...prev,
          {
            text: message,
            sender: 'user',
            imageUrl: signedUrl,
            timestamp: new Date().toISOString(),
          },
        ]));
      }

      // Use the journey supplied for this upload immediately. React state is
      // asynchronous, so reading only activeJourneyRef here could use the
      // previous journey and drop an inspiration/range reference.
      const response = await axios.post(getChatRequestUrl(), {
        message: message,
        userId: userId,
        image_data: imageBase64,  // Send base64 - avoids Supabase auth issues
        image_url: signedUrl,  // For storage/display only
        ...(scanImages ? { images: scanImages } : {}),  // all scan angles → multi-angle read
        // Guided skin-capture asks for a fresh 'skin' analysis so the backend
        // returns the '## Skin Tone Analysis' markdown that renders the card
        // (and doesn't short-circuit to "follow-up?" on a recent analysis).
        // Default path is unchanged: 'auto' + no freshAnalysis.
        analysis_type: opts.analysisType || 'auto',
        ...(opts.imageRole ? { image_role: opts.imageRole } : {}),
        ...(opts.analysisScope ? { analysis_scope: opts.analysisScope } : {}),
        ...(opts.productTitle ? { journey_product: opts.productTitle } : {}),
        ...(opts.catalogProductId ? { catalog_product_id: opts.catalogProductId } : {}),
        ...(opts.searchedProduct ? { searched_product: opts.searchedProduct } : {}),
        ...(referenceImageUrl ? { journey_reference_url: referenceImageUrl } : {}),
        ...(referenceKind ? { journey_reference_kind: referenceKind } : {}),
        ...(currentSessionIdRef.current ? { sessionId: currentSessionIdRef.current } : {}),
        ...(opts.freshAnalysis ? { freshAnalysis: true } : {}),
        // Post-capture intake (age / sensitivities). The backend folds this into
        // the SAME analysis prompt, so the read is genuinely personalised rather
        // than merely described as such. Omitted entirely when not collected.
        ...(opts.intake ? { intake: opts.intake } : {}),
        // A bare photo (no typed question) still needs words for the backend's
        // flexible routing to fire — with an empty prompt it falls back to the
        // legacy pre-routed analyzer, which is what made a generic upload guess.
        // opts.promptOverride supplies a neutral default that is sent ONLY to the
        // backend; the visible user bubble stays whatever the user actually typed.
        user_prompt: (userMessage && userMessage.trim()) ? userMessage : (opts.promptOverride || ''),
        accessToken: accessToken
      }, {
        timeout: CHAT_REQUEST_TIMEOUT_MS,
        signal: requestTimeout.signal,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        const consumedInspirationReference =
          opts.imageRole === IMAGE_ROLES.GENERIC_CHAT_PHOTO && Boolean(referenceImageUrl);
        if (response.data.journey_context || consumedInspirationReference) {
          const nextJourney = {
            ...(activeJourneyRef.current || {}),
            ...(response.data.journey_context || {}),
          };
          // The earlier inspiration is paired with the next uploaded photo
          // once. Keeping it active indefinitely can attach it to unrelated
          // later requests in the same chat.
          if (consumedInspirationReference) {
            delete nextJourney.referenceImageUrl;
            delete nextJourney.referenceKind;
          }
          setActiveJourney(nextJourney);
        }
        if (opts.imageRole) trackOutcomeResultDelivered(opts.imageRole);
        // Check if this is a structured product analysis
        const product_data = response.data.product_data;
        
        if (product_data) {
          // Flex path: the model may have ANSWERED the user's question
          // (reply.advice = answer + reasoning). Lead with that structured answer —
          // rendered as prose + "Show full reasoning" — then the product card below,
          // mirroring how the skin read shows an answer above its card.
          const _reply = response.data.reply;
          if (_reply && typeof _reply === 'object' && (_reply.advice || _reply.answer)) {
            setMessages(prev => [...prev, {
              text: _reply,
              sender: 'assistant',
              isStructured: true,
              timestamp: new Date().toISOString(),
            }]);
          }

          // Structured product analysis with ProductAnalysisCard

          // Handle both single product and multiple products
          const products = product_data.products || [product_data];
          
          // Add a product card for each product
          products.forEach((product, index) => {
            setMessages(prev => {
              return [
                ...prev,
                {
                  text: {
                    type: 'product_analysis',
                    data: {
                      score: product.score || 0,
                      compatibility: product.compatibility || 'medium',
                      whatsGreat: product.pros || [],  // Map pros → whatsGreat
                      concerns: product.concerns || [],
                      reasoning: product.reasoning || '',
                      best_for: product.best_for || 'General use',
                      ingredients_highlight: product.ingredients_highlight || '',
                      ingredient_insight: product.ingredient_insight || '',
                      key_ingredients: product.key_ingredients || [],
                      productType: product.category || '',  // Add product type
                      // Add recommendation object if we have reasoning
                      recommendation: product.reasoning ? {
                        verdict: product.compatibility === 'high' ? 'recommended' : 
                                product.compatibility === 'medium' ? 'proceed-with-caution' : 'not-recommended',
                        reasoning: product.reasoning,
                        usage: product.best_for
                      } : null
                    },
                    productName: product.name || product.product_name || product.productName || 'Unknown Product',
                    productBrand: product.brand || product.productBrand || product.product_brand || '',
                    productImage: signedUrl
                  },
                  sender: 'assistant',
                  timestamp: new Date().toISOString()
                }
              ];
            });
          });
        } else {
          // Flex path (e.g. face + a question): the model's answer is in
          // response.data.reply, not .message. Lead with that structured answer —
          // prose + "Show full reasoning" — then the skin read / check-in below.
          // Guided scans keep their single message (message = the read, no reply).
          const _flexReply = response.data.reply;
          if (!response.data.message && _flexReply && typeof _flexReply === 'object'
              && (_flexReply.advice || _flexReply.answer)) {
            // When the turn is about a product (backend sets imageType 'product'),
            // flag it so the answer bubble can offer "See full formula analysis" →
            // opens the Product Analysis section. Gated so the CTA never shows on a
            // skin read or a general question.
            const _isProductTurn = response.data.imageType === 'product';
            setMessages(prev => [...prev, {
              text: _flexReply,
              sender: 'assistant',
              isStructured: true,
              timestamp: new Date().toISOString(),
              ...(_isProductTurn ? { productContext: true } : {}),
              ...(opts.imageRole === IMAGE_ROLES.INSPIRATION_REFERENCE ? { journeyAction: 'personalise_inspiration' } : {}),
              ...(response.data.journeyAction ? { journeyAction: response.data.journeyAction } : {}),
              ...(response.data.shadeMatches ? { shadeMatches: response.data.shadeMatches } : {}),
              ...(response.data.journey_context ? { journeyContext: response.data.journey_context } : {}),
            }]);
          }
          // Regular text response (skin analysis or other). Only render it when
          // there's real content — a pure flex answer (e.g. makeup critique: reply
          // present, no message/skinAnalysis) would otherwise push an empty bubble
          // that renders as "Message could not be displayed".
          const _hasReadContent = !!(response.data.message || response.data.skinAnalysis || response.data.skinCheckin || (opts.guidedScan && signedUrl));
          if (_hasReadContent) setMessages(prev => {
            return [
              ...prev,
              {
                text: response.data.message,
                sender: 'assistant',
                timestamp: new Date().toISOString(),
                skinAnalysis: response.data.skinAnalysis || null,
                skinCheckin: response.data.skinCheckin || null,
                ...(opts.imageRole === IMAGE_ROLES.INSPIRATION_REFERENCE ? { journeyAction: 'personalise_inspiration' } : {}),
                ...(response.data.journeyAction ? { journeyAction: response.data.journeyAction } : {}),
                ...(response.data.shadeMatches ? { shadeMatches: response.data.shadeMatches } : {}),
                ...(response.data.journey_context ? { journeyContext: response.data.journey_context } : {}),
                // Guided scan: carry the captured photo as the card's hero (and
                // mark it a scan) so the result renders as one photo-led "Skin
                // scan" card instead of a raw selfie bubble + a text card.
                ...(opts.guidedScan && signedUrl ? {
                  scanImageUrl: signedUrl,
                  isSkinScan: true,
                  // Multi-angle scan: carry the angle count + thumbnails (live only,
                  // not persisted) so the card reads as a real multi-photo scan, not
                  // one selfie. Falls away to a single hero when only the front exists.
                  ...(scanImages && scanImages.length > 1 ? {
                    scanPhotoCount: scanImages.length,
                    scanAngleImages: scanImages.map((b) => `data:image/jpeg;base64,${b}`),
                  } : {}),
                } : {}),
              }
            ];
          });
          // Auto-populate profile fields. Prefer backend-supplied structured
          // data; fall back to scraping the rendered markdown so the profile
          // always matches what the SkinAnalysisCard just displayed.
          const fromBackend = response.data.skinAnalysis;
          // #65: the backend now returns skinAnalysis.analysis as a MARKDOWN STRING
          // (not a structured object). The card parses that markdown — the journal
          // must too, or `parsed` stays null and no entry is written (this is why
          // Skin Journal scans stopped syncing). Parse the markdown from
          // skinAnalysis.analysis first, then fall back to the message body.
          const backendMarkdown = (fromBackend && typeof fromBackend.analysis === 'string')
            ? parseSkinAnalysisFromMarkdown(fromBackend.analysis)
            : null;
          const fromMarkdown = parseSkinAnalysisFromMarkdown(response.data.message);
          const parsed = (fromBackend?.analysis && typeof fromBackend.analysis === 'object')
            ? fromBackend.analysis
            : (fromBackend && (fromBackend.skin_tone || fromBackend.skin_type))
              ? fromBackend
              : (backendMarkdown || fromMarkdown);
          const mayPersistFullSkin = canPersistFullSkinResult(opts.imageRole);
          if (parsed && mayPersistFullSkin) saveAnalysisToProfile(parsed);

          // Email guests receive the result before the account ask. Converting
          // the anonymous Supabase identity keeps the same user id, so the scan
          // already attached to that identity remains available after signup.
          if (isAnonymous && saveAfterFirstScan && opts.guidedScan && !emailSavePromptShown.current) {
            emailSavePromptShown.current = true;
            setUpgradeStartsOnSignIn(false);
            setUpgradeSkipsPitch(true);
            setShowUpgradeModal(true);
          }

          // Auto-create a Skin Journal entry from the chat analysis so the
          // user's journal stays in sync with what they ran in chat.
          //
          // #65 GUARD: this gate used to be the ONLY path to a journal entry.
          // When a backend shape change made `parsed` unreadable it quietly
          // evaluated false — no entry, no error, nothing in the logs — and
          // Skin Journal syncing was dead for three days before anyone noticed.
          // A completed skin read must never write nothing: the photo is the
          // artifact worth keeping even when we can't read tone/type out of it.
          // So when the backend says this WAS a skin read (`skinAnalysis`
          // present) but the parse came back thin, we write a degraded entry
          // and report the anomaly instead of silently skipping.
          const parseUsable = !!(parsed && (parsed.skin_tone || parsed.skin_type));
          if (parseUsable && mayPersistFullSkin) {
            createSkinJournalEntryFromAnalysis({
              parsed,
              analysisText: response.data.message,
              imageUrl: signedUrl,
              photoPaths: opts.photoPaths,
            });
          } else if (fromBackend && mayPersistFullSkin) {
            const detail = `analysis=${typeof fromBackend.analysis}, parsedKeys=${parsed ? Object.keys(parsed).join('|') || 'empty' : 'null'}`;
            console.error('🪞 Skin Journal: skin read completed but tone/type unreadable — writing degraded entry.', detail);
            trackError('journal_parse_degraded', detail);
            createSkinJournalEntryFromAnalysis({
              parsed: parsed || {},
              analysisText: response.data.message,
              imageUrl: signedUrl,
              photoPaths: opts.photoPaths,
            });
          }
        }
      } else {
        throw new Error(response.data.message || 'Analysis failed');
      }

    } catch (error) {
      console.error('Image analysis error:', error);
      reportError(error, 'image_analysis', {
        image_role: opts.imageRole || 'unspecified',
        status: error?.response?.status || 0,
        timed_out: requestTimeout?.didTimeout() || false,
      });
      if (opts.imageRole) trackOutcomeJourneyFailed(opts.imageRole, 'analysis_failed');
      // Check for quota/service-unavailable errors (axios wraps 503 responses)
      const errorData = error.response?.data;
      const isQuotaError = errorData?.error_type === 'quota_error';
      const isTimeout = requestTimeout?.didTimeout() || error?.code === 'ECONNABORTED';
      const errorMessage = isQuotaError
        ? { type: 'service_unavailable' }
        : isTimeout
          ? "I couldn't retrieve that analysis. The photo is still in this conversation, so please try again when you're ready."
          : 'I couldn\'t analyse that image. Please try again, or choose another photo.';
      setMessages(prev => [
        ...prev.filter(msg => !msg.isLoading),
        { text: errorMessage, sender: 'assistant' }
      ]);
    } finally {
      requestTimeout?.clear();
      setIsLoading(false);
      setStreamStatus('');
    }
  };

  // Send multiple images for batch analysis (all at once)
  const sendMultipleImagesForAnalysis = async (processedImages, context = '', analysisType = 'auto') => {
    let requestTimeout = null;
    try {
      const { userId, accessToken } = processedImages[0]; // All should have same user/token

      // Show bottom loading indicator (with TypingFact) during image analysis
      setIsLoading(true);
      setStreamStatus('');
      requestTimeout = createRequestTimeout({
        onSlow: () => setStreamStatus('Still working…'),
      });

      // Convert all images to base64
      const imageBase64Array = await Promise.all(
        processedImages.map(img => fileToBase64(img.file))
      );
      
      // Send all images as a batch for holistic analysis
      const requestData = {
        message: context ? 
          `${context}\n\nI've uploaded ${processedImages.length} images for ${analysisType} analysis. Please analyze all images together and provide a comprehensive, holistic analysis.` :
          `I've uploaded ${processedImages.length} images for ${analysisType} analysis. Please analyze all images together and provide a comprehensive, holistic analysis.`,
        userId: userId,
        images_data: imageBase64Array, // Send base64 - avoids Supabase auth issues
        image_urls: processedImages.map(img => img.signedUrl), // For storage/display only
        analysis_type: analysisType,
        freshAnalysis: true,
        batchAnalysis: true,
        totalImages: processedImages.length,
        ...(currentSessionIdRef.current ? { sessionId: currentSessionIdRef.current } : {}),
      };


      const response = await fetch(getChatRequestUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(requestData),
        signal: requestTimeout.signal,
      });

      if (!response.ok) {
        // Read the body so we can surface friendly quota/service error messages
        let errorBody = {};
        try { errorBody = await response.json(); } catch (_) {}
        if (errorBody.error_type === 'quota_error') {
          setMessages(prev => [
            ...prev.filter(msg => !msg.isLoading),
            { text: { type: 'service_unavailable' }, sender: 'assistant' }
          ]);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // Add all images to uploaded images state
      processedImages.forEach((image, index) => {
        setUploadedImages(prev => [...prev, {
          id: Date.now() + Math.random() + index,
          file: image.file,
          url: image.signedUrl,
          timestamp: new Date().toISOString()
        }]);
      });

      // Remove loading messages
      setMessages(prev => prev.filter(msg => !msg.isLoading));

      // Handle the batch response - use the raw reply text directly for auto-analysis
      const analysisText = responseData.reply || responseData.message;
      
      // For auto-analysis, just add the raw text without structuring
      if (analysisType === 'auto') {
        // Remove loading messages
        setMessages(prev => [
          ...prev.filter(msg => !msg.isLoading),
          { 
            text: analysisText, 
            sender: 'assistant',
            timestamp: new Date().toISOString(),
            images: processedImages.map(img => ({
              url: img.signedUrl,
              timestamp: new Date().toISOString()
            }))
          }
        ]);
        
        setAnalysisProgress(100);
        setShowAnalyzedPhotos(true);
        
        // Reset progress after a delay
        setTimeout(() => {
          setAnalysisProgress(0);
        }, 3000);
        
        return; // Exit early for auto-analysis
      }
      
      // Handle legacy structured response for specific analysis types
      let structuredResponse = {
        advice: null,
        products: null,
        routine: null
      };

      if (analysisType === 'skin') {
        // For skin analysis, use the batch response directly
        structuredResponse.advice = analysisText;
        structuredResponse.routine = `*This analysis considers all ${processedImages.length} uploaded images together for comprehensive skin assessment.*`;
      } else if (analysisType === 'product') {
        // For product analysis, use the batch response directly
        structuredResponse.products = analysisText;
        structuredResponse.advice = `*This analysis considers all ${processedImages.length} uploaded images together for comprehensive product identification.*`;
      }

      // Add structured response with images
      setMessages(prev => [
        ...prev,
        { 
          text: structuredResponse, 
          sender: 'assistant',
          timestamp: new Date().toISOString(),
          isStructured: true,
          productAnalysis: responseData.productAnalysis,
          skinAnalysis: responseData.skinAnalysis,
          skinCheckin: responseData.skinCheckin || null,
          images: processedImages.map(img => ({
            url: img.signedUrl,
            type: analysisType,
            timestamp: new Date().toISOString()
          }))
        }
      ]);

      // Track analysis completion
      if (analysisType === 'face' && responseData.skinAnalysis) {
        trackSkinPhotoAnalysis();
        trackSkinAnalysisComplete({
          skinType: responseData.skinAnalysis.skin_type,
          concerns: responseData.skinAnalysis.concerns || []
        });
        // (Removed) We no longer pin skin_type/main_concern as GA user properties —
        // health-adjacent data should not be attached to a Google Analytics profile.
        // Auto-populate profile fields from skin analysis results
        saveAnalysisToProfile(responseData.skinAnalysis);
      } else if (analysisType === 'product' && responseData.productAnalysis) {
        trackProductPhotoAnalysis();
      }

      setAnalysisProgress(100);
      setShowAnalyzedPhotos(true);

      // (Register gate doesn't fire here — it's the 7-interaction effect. This
      // analysis is one of those 7. See REGISTER_GATE_INTERACTIONS.)

      // Reset progress after a delay
      setTimeout(() => {
        setAnalysisProgress(0);
      }, 3000);

    } catch (error) {
      console.error('Error in sequential analysis:', error);
      reportError(error, 'multi_image_analysis', {
        image_count: processedImages.length,
        timed_out: requestTimeout?.didTimeout() || false,
      });
      const isTimeout = requestTimeout?.didTimeout() || error?.name === 'AbortError';
      setMessages(prev => [
        ...prev.filter(msg => !msg.isLoading),
        {
          text: isTimeout
            ? "I couldn't retrieve that analysis. The photos are still in this conversation, so please try again when you're ready."
            : 'I couldn\'t analyse those images. Please try again, or choose different photos.',
          sender: 'assistant'
        }
      ]);
      throw error;
    } finally {
      requestTimeout?.clear();
      setIsLoading(false);
      setStreamStatus('');
    }
  };

  const uploadSingleImage = async (file, skipLoadingMessage = false, opts = {}) => {
    if (reopenGate()) return;
    try {

      // Validate file first
      validateImageFile(file);

      // Get session from Supabase useAuth
      const { tokens, userId } = await getAuthSession();
      const accessToken = tokens?.accessToken;

      if (!accessToken || !userId) {
        throw new Error('No access token or userId found');
      }


      // Step 1: Resize and optimize the image
      const optimizedImage = await resizeAndOptimizeImage(file);

      // Step 2: Upload to Supabase Storage (private bucket)
      const filePath = `uploaded_images/${userId}/${Date.now()}_${optimizedImage.name || file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, optimizedImage, {
          cacheControl: '3600',
          upsert: false,
          contentType: optimizedImage.type || file.type,
        });
      if (uploadError) throw uploadError;

      // Guided scan: also persist the OTHER captured angles (both cheeks, chin)
      // to the same bucket so the WHOLE scan is saved, not just the straight-on
      // shot — and the Skin Journal can show them. scanAngles[0] is the front we
      // just uploaded, so skip it. Angle saves are best-effort (non-fatal).
      const photoPaths = [filePath];
      if (opts.guidedScan && Array.isArray(opts.scanAngles) && opts.scanAngles.length > 1) {
        for (let i = 1; i < opts.scanAngles.length; i++) {
          try {
            const angleOpt = await resizeAndOptimizeImage(opts.scanAngles[i]);
            const anglePath = `uploaded_images/${userId}/${Date.now()}_a${i}_${angleOpt.name || opts.scanAngles[i].name}`;
            const { error: angleErr } = await supabase.storage
              .from('images')
              .upload(anglePath, angleOpt, {
                cacheControl: '3600',
                upsert: false,
                contentType: angleOpt.type || opts.scanAngles[i].type,
              });
            if (angleErr) console.warn('Scan angle upload failed (non-fatal):', angleErr);
            else photoPaths.push(anglePath);
          } catch (e) {
            console.warn('Scan angle upload error (non-fatal):', e);
          }
        }
      }

      // Step 3: Generate a signed URL (valid for 7 days, will auto-refresh when needed)
      const { data, error: signError } = await supabase.storage
        .from('images')
        .createSignedUrl(filePath, 604800); // 7 days
      if (signError) throw signError;
      const signedUrl = data.signedUrl;

      // Send image directly to backend for analysis (skip loading message if from sequential upload)
      await sendImageForAnalysis(optimizedImage, signedUrl, opts.displayPrompt || input || '', skipLoadingMessage, { ...opts, photoPaths });

      // Add image to uploaded images list
      setUploadedImages(prev => [...prev, {
        id: Date.now(),
        url: signedUrl,
        name: file.name,
        timestamp: new Date().toISOString()
      }]);

      // Clear input after successful upload
      setInput('');

    } catch (error) {
      console.error('Upload error:', error);
      reportError(error, 'image_upload', {
        image_role: opts.imageRole || 'unspecified',
      });
      if (opts.imageRole) trackOutcomeJourneyFailed(opts.imageRole, 'upload_failed');
      setMessages(prev => [
        ...prev,
        { text: 'I couldn\'t upload that image. Please try again, or choose another photo.', sender: 'assistant' }
      ]);
    }
  };

  // Guided skin-capture entry: analyze the straight-on capture as a FRESH SKIN
  // analysis so the backend returns the '## Skin Tone Analysis' markdown that
  // renders the SkinAnalysisCard (not prose / "follow-up?"). Consent is already
  // gated in ChatUI's handleProceedToUpload before the camera opens. The other
  // scan angles are captured for the future multi-photo backend read; today's
  // single-photo analysis runs on the straight-on shot.
  const uploadCaptureImage = async (file, scanAngles, intake) => {
    return uploadSingleImage(file, false, {
      analysisType: 'skin',
      freshAnalysis: true,
      guidedScan: true,
      scanAngles,
      intake,
      imageRole: IMAGE_ROLES.FULL_SKIN_SCAN,
    });
  };

  // New function to handle single image upload with analysis type
  const uploadSingleImageWithAnalysisType = async (file, analysisType) => {
    if (reopenGate()) return;
    try {

      // Get session from Supabase useAuth
      const { tokens, userId } = await getAuthSession();
      const accessToken = tokens?.accessToken;

      if (!accessToken || !userId) {
        throw new Error('No access token or userId found');
      }

      // Upload to Supabase Storage (private bucket)
      const filePath = `uploaded_images/${userId}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type,
        });
      if (uploadError) throw uploadError;

      // Generate a signed URL (valid for 7 days, will auto-refresh when needed)
      const { data, error: signError } = await supabase.storage
        .from('images')
        .createSignedUrl(filePath, 604800); // 7 days
      if (signError) throw signError;
      const signedUrl = data.signedUrl;

      // Show bottom loading indicator (with TypingFact) during image analysis
      setIsLoading(true);

      // Send image to conversation endpoint with analysis type and user prompt
      const message = userImagePrompt ? 
        `${userImagePrompt}\n\nI've uploaded an image for ${analysisType} analysis. Please analyze what you see and provide helpful insights based on the image content.` :
        `I've uploaded an image for ${analysisType} analysis. Please analyze what you see and provide helpful insights based on the image content.`;
      
      const response = await axios.post(getChatRequestUrl(), {
        message: message,
        userId: userId,
        image_data: signedUrl,  // Send the signed URL as image data
        analysis_type: analysisType,  // Add analysis type parameter
        user_prompt: userImagePrompt,  // Include user's specific prompt
        freshAnalysis: true,  // Flag to indicate this is a fresh image analysis
        ...(currentSessionIdRef.current ? { sessionId: currentSessionIdRef.current } : {}),
      }, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });


      // Add image to uploaded images list (simple tracking)
      const newImageData = {
        url: signedUrl,
        timestamp: new Date().toISOString(),
        analysis: response.data
      };
      setUploadedImages(prev => [...prev, newImageData]);
      
      // Show analyzed photos banner and auto-hide after 30 seconds
      setShowAnalyzedPhotos(true);
      setTimeout(() => {
        setShowAnalyzedPhotos(false);
      }, 30000); // 30 seconds

      // Safely remove loading message and add upload message with product info
      setMessages(prev => {
        // Find the last loading message to remove
        const lastIndex = prev.length - 1;
        const isLastMessageLoading = prev[lastIndex]?.isLoading === true && prev[lastIndex]?.sender === 'assistant';
        
        const messagesWithoutLoading = isLastMessageLoading ? prev.slice(0, -1) : prev;
        
        // Extract product information from analysis if available
        let productName = 'Unknown Product';
        let productBrand = 'Unknown Brand';
        
        if (response.data.productAnalysis) {
          // Try to extract product name and brand from the analysis text
          const analysis = response.data.productAnalysis.analysis;
          
          // More comprehensive extraction patterns for structured format
          const namePatterns = [
            /Product name:\s*([^\n\r]+)/i,
            /Name:\s*([^\n\r]+)/i,
            /Product:\s*([^\n\r]+)/i
          ];
          
          const brandPatterns = [
            /Brand name:\s*([^\n\r]+)/i,
            /Brand:\s*([^\n\r]+)/i
          ];
          
          // Try each pattern for product name
          for (const pattern of namePatterns) {
            const match = analysis.match(pattern);
            if (match && match[1].trim() && 
                !match[1].trim().toLowerCase().includes('not clearly visible') &&
                match[1].trim() !== 'Unknown Product') {
              productName = match[1].trim();
              break;
            }
          }
          
          // Try each pattern for brand
          for (const pattern of brandPatterns) {
            const match = analysis.match(pattern);
            if (match && match[1].trim() && 
                !match[1].trim().toLowerCase().includes('not clearly visible') &&
                match[1].trim() !== 'Unknown Brand') {
              productBrand = match[1].trim();
              break;
            }
          }
          
        }
        
        return [
          ...messagesWithoutLoading,
          { 
            text: `I uploaded a photo for analysis`, 
            sender: 'user',
            imageUrl: signedUrl,
            productName: productName,
            productBrand: productBrand
          }
        ];
      });

      // Handle analysis response from conversation endpoint
      let analysisMessage = response.data.reply;
      let isStructured = false;
      
      // Check if the response is structured JSON
      if (typeof analysisMessage === 'object' && analysisMessage !== null) {
        isStructured = true;
      } else if (typeof analysisMessage === 'string') {
        analysisMessage = stripLeadingToolCallJson(analysisMessage.trim());
        const parsed = tryParseStructured(analysisMessage);
        if (parsed) {
          analysisMessage = parsed;
          isStructured = true;
        } else {
        }
      }
      
      // If not structured, create structured response based on analysis type
      if (!isStructured) {
        let structuredResponse = {
          advice: null,
          products: null,
          routine: null
        };

        if (analysisType === 'skin') {
          structuredResponse.advice = `**Skin Analysis:**\n\n${analysisMessage}\n\n*Based on your skin analysis, consider these routine adjustments:*\n• Review your current skincare products for compatibility\n• Adjust frequency of active ingredients if needed\n• Monitor skin response over the next few weeks`;
          structuredResponse.routine = `**Recommended Routine Updates:**\n\n*Based on your skin analysis, consider these routine adjustments:*\n• Review your current skincare products for compatibility\n• Adjust frequency of active ingredients if needed\n• Monitor skin response over the next few weeks`;
        } else if (analysisType === 'product') {
          structuredResponse.products = `**Product Analysis:**\n\n${analysisMessage}`;
          structuredResponse.advice = `**Product Recommendations:**\n\n*Based on your product analysis:*\n• Consider how these products fit into your current routine\n• Check for ingredient compatibility with existing products\n• Start with patch testing before full application`;
        }

        analysisMessage = structuredResponse;
        isStructured = true;
      }
      
      setMessages(prev => [
        ...prev,
        {
          text: analysisMessage,
          sender: 'assistant',
          timestamp: new Date().toISOString(),
          isStructured: isStructured,
          productAnalysis: response.data.productAnalysis,
          skinAnalysis: response.data.skinAnalysis,
          images: [{
            url: signedUrl,
            type: analysisType,
            timestamp: new Date().toISOString()
          }]
        }
      ]);

      // Auto-populate profile fields from skin analysis results
      if (analysisType !== 'product' && response.data.skinAnalysis) {
        saveAnalysisToProfile(response.data.skinAnalysis);
      }

    } catch (error) {
      console.error('Upload error:', error);
      reportError(error, 'typed_image_upload', { analysis_type: analysisType || 'unspecified' });
      const errorData = error.response?.data;
      const isQuotaError = errorData?.error_type === 'quota_error';
      const errorMessage = isQuotaError
        ? { type: 'service_unavailable' }
        : 'I couldn\'t upload that image. Please try again, or choose another photo.';
      setMessages(prev => [
        ...prev.filter(msg => !msg.isLoading),
        { text: errorMessage, sender: 'assistant' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Scrape skin tone / undertone / skin type / concerns from the markdown
  // chat response. The SkinAnalysisCard does the same parsing for display —
  // this is the data-side mirror so the profile reflects what the user just
  // saw on screen, even when the backend doesn't return structured
  // `skinAnalysis.analysis` fields. Returns null if no skin sections are found.
  const parseSkinAnalysisFromMarkdown = (text) => {
    if (typeof text !== 'string') return null;
    if (!text.includes('Skin Tone Analysis') && !text.includes('Skin Type Classification')) return null;

    const sectionAfter = (header) => {
      const idx = text.toLowerCase().indexOf(header.toLowerCase());
      if (idx === -1) return '';
      const rest = text.slice(idx + header.length);
      const next = rest.search(/\n##\s/);
      return next === -1 ? rest : rest.slice(0, next);
    };

    const toneSec = sectionAfter('## Skin Tone Analysis');
    const typeSec = sectionAfter('## Skin Type Classification');
    const concernSec = sectionAfter('## Detected Concerns') || sectionAfter('## Concerns');

    const toneMatch = toneSec.match(/(fair|light|medium|tan|deep|rich)/i);
    const undertoneMatch = toneSec.match(/(warm|cool|neutral|olive)/i);
    const typeMatch = typeSec.match(/(oily|dry|combination|normal|sensitive)/i);

    // Concern names appear as **Name** bullets. Pull all bolded labels.
    const concernNames = [];
    if (concernSec && !/no\s+(major\s+)?concerns/i.test(concernSec) && !/none\s+detected/i.test(concernSec)) {
      const re = /\*\*([^*]+?)\*\*/g;
      let m;
      while ((m = re.exec(concernSec)) !== null) {
        const name = m[1].split('(')[0].trim();
        if (name) concernNames.push(name);
      }
    }

    const result = {};
    if (toneMatch) result.skin_tone = toneMatch[1];
    if (undertoneMatch) result.undertone = undertoneMatch[1];
    if (typeMatch) result.skin_type = typeMatch[1];
    if (concernNames.length > 0) result.concerns = concernNames;
    return Object.keys(result).length > 0 ? result : null;
  };

  // Auto-saves skin analysis results back to the user's profile in Supabase.
  // Called after every face photo analysis so the profile stays current without
  // the user having to manually fill in fields they just had analysed.
  //
  // The backend wraps the analysis object: `{ type, analysis: {...}, imageType }`.
  // Older callers passed the inner `analysis` object directly. Accept either
  // shape so this keeps working regardless of how the call site evolves.
  const saveAnalysisToProfile = (skinAnalysisRaw) => {
    // Slice 3: analysis → profile is now written SOLELY by the backend
    // (update_user_profile_from_analysis), which respects the user_set lock and,
    // per A-prime, fires whenever structured skin fields are parsed. This frontend
    // duplicate ignored the lock (silently overwriting the user's corrections) and
    // auto-filled primary_concerns (now user-controlled only — Codex Fork-1 = B).
    // Disabled; the three call sites are harmless no-ops (full removal in cleanup).
    return;
    // eslint-disable-next-line no-unreachable
    if (isAnonymous || !currentUserId || !skinAnalysisRaw) return;
    const skinAnalysis = skinAnalysisRaw?.analysis && typeof skinAnalysisRaw.analysis === 'object'
      ? skinAnalysisRaw.analysis
      : skinAnalysisRaw;
    if (!skinAnalysis || typeof skinAnalysis !== 'object') return;

    const SKIN_TONE_MAP = {
      fair: 'Fair', light: 'Light', medium: 'Medium', tan: 'Tan', deep: 'Deep', rich: 'Rich',
    };
    const UNDERTONE_MAP = {
      cool: 'Cool', warm: 'Warm', neutral: 'Neutral', olive: 'Olive',
    };
    const SKIN_TYPE_MAP = {
      normal: 'Normal', oily: 'Oily', dry: 'Dry', combination: 'Combination', sensitive: 'Sensitive',
    };
    // Canonical concerns the profile UI knows about. AI may return free-text
    // variants — we coerce by lowercased match so "fine lines" still maps.
    const CONCERN_CANON = [
      'Acne', 'Dark Spots', 'Dullness', 'Fine Lines', 'Redness',
      'Large Pores', 'Uneven Texture', 'Hyperpigmentation', 'Dehydration', 'Sensitivity',
    ];
    const profileUpdate = {};
    if (skinAnalysis.skin_type) {
      const normalized = SKIN_TYPE_MAP[String(skinAnalysis.skin_type).toLowerCase()] || skinAnalysis.skin_type;
      profileUpdate.skin_type = normalized;
    }
    if (skinAnalysis.skin_tone) {
      const normalized = SKIN_TONE_MAP[String(skinAnalysis.skin_tone).toLowerCase()] || skinAnalysis.skin_tone;
      profileUpdate.skin_tone = normalized;
    }
    if (skinAnalysis.undertone) {
      const normalized = UNDERTONE_MAP[String(skinAnalysis.undertone).toLowerCase()] || skinAnalysis.undertone;
      profileUpdate.undertone = normalized;
    }
    // Concerns: coerce to canonical labels, drop unknowns, dedupe, cap at 5
    // (the focus-list max in PersonalProfile). Backend currently does NOT write
    // primary_concerns from analysis, so the frontend filling it gives the
    // user a head start they can edit.
    if (Array.isArray(skinAnalysis.concerns) && skinAnalysis.concerns.length > 0) {
      const canon = skinAnalysis.concerns
        .map(c => CONCERN_CANON.find(k => k.toLowerCase() === String(c).toLowerCase()))
        .filter(Boolean);
      const deduped = Array.from(new Set(canon)).slice(0, 5);
      if (deduped.length > 0) profileUpdate.primary_concerns = deduped;
    }
    if (Object.keys(profileUpdate).length === 0) return;
    supabase.from('user_profiles')
      .upsert({ user_id: currentUserId, ...profileUpdate, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      .then(() => console.log('✅ Profile auto-updated from skin analysis:', profileUpdate))
      .catch(err => console.error('❌ Failed to auto-update profile from analysis:', err));
  };

  // Insert a Skin Journal entry derived from a chat skin analysis. We use the
  // structured fields the SkinAnalysisCard parsed so the journal mirrors what
  // the user saw in the chat. Source is 'chat_analysis' so we can distinguish
  // these from manual entries later (and avoid double-creating if the same
  // image gets re-analysed in a session).
  const createSkinJournalEntryFromAnalysis = async ({ parsed, analysisText, imageUrl, photoPaths: providedPaths }) => {
    if (isAnonymous || !currentUserId) return;
    try {
      // Prefer the explicit list of saved storage paths — a guided scan saves the
      // front AND every angle. Fall back to pulling the single path out of the
      // signed URL for the plain single-photo path (mirrors the AddEntry flow).
      let photoPaths = Array.isArray(providedPaths) ? providedPaths.filter(Boolean) : [];
      if (photoPaths.length === 0 && typeof imageUrl === 'string') {
        const m = imageUrl.match(/\/storage\/v1\/object\/sign\/[^/]+\/(.+?)\?/);
        if (m) photoPaths = [decodeURIComponent(m[1])];
      }

      // Build a brief AI summary from the concerns the card extracted. Concerns
      // come back as "Name (Severity, Confidence)" strings via the markdown
      // parser; we keep the names and let the journal feed render them as
      // bullets via parseSummaryBullets.
      const concerns = Array.isArray(parsed.concerns) ? parsed.concerns : [];
      const summaryBullets = [];
      if (parsed.skin_type) summaryBullets.push(`Skin type read as ${parsed.skin_type}.`);
      if (parsed.skin_tone || parsed.undertone) {
        const tone = [parsed.skin_tone, parsed.undertone && `${parsed.undertone} undertone`]
          .filter(Boolean)
          .join(', ');
        summaryBullets.push(`Tone: ${tone}.`);
      }
      if (concerns.length > 0) {
        summaryBullets.push(`Concerns flagged: ${concerns.slice(0, 5).join(', ')}.`);
      }
      const aiSummary = summaryBullets.map(b => `- ${b}`).join('\n');

      // Dedupe: don't insert if the same image already has an entry today.
      // (Chat analysis can be retried; we don't want a journal spam.)
      const since = new Date(Date.now() - 60 * 60 * 1000).toISOString(); // last hour
      const { data: recent } = await supabase
        .from('skin_journal_entries')
        .select('id, photo_paths')
        .eq('user_id', currentUserId)
        .eq('source', 'chat_analysis')
        .gte('created_at', since)
        .limit(5);
      if (Array.isArray(recent) && photoPaths[0]) {
        const dup = recent.some(r => Array.isArray(r.photo_paths) && r.photo_paths.includes(photoPaths[0]));
        if (dup) {
          console.log('🪞 Skipping duplicate Skin Journal entry for same chat photo');
          return;
        }
      }

      const payload = {
        user_id: currentUserId,
        source: 'chat_analysis',
        photo_paths: photoPaths,
        ai_summary: aiSummary || null,
        notes: null,
        // We don't have a 1–10 overall score from the chat path; leave the
        // overall slider at the AddEntry default if the user edits later.
        overall_score: null,
        skin_metrics: null,
      };

      const { error: insertError } = await supabase
        .from('skin_journal_entries')
        .insert(payload);
      if (insertError) {
        console.error('❌ Failed to auto-create Skin Journal entry:', insertError);
      } else {
        console.log('✅ Skin Journal entry auto-created from chat analysis');
      }
    } catch (err) {
      console.error('❌ createSkinJournalEntryFromAnalysis error:', err);
    }
  };

  // OPTIMIZED: Resize and optimize image with better performance
  const resizeAndOptimizeImage = async (file) => {
    // Check if it's a HEIC file and convert to JPEG first
    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
      try {
        const heic2any = (await import('heic2any')).default;
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.9
        });
        // Convert blob to file
        file = new File([convertedBlob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
      } catch (error) {
        console.error('HEIC conversion failed:', error);
        throw new Error('Failed to convert HEIC image. Please try a different format.');
      }
    }
    
    return new Promise((resolve, reject) => {
      
      // OPTIMIZATION: Skip compression for small files to avoid unnecessary processing
      if (file.size < 500000) { // Less than 500KB
        resolve(file);
        return;
      }
      
      // Use Web Workers for image processing to avoid blocking UI
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {

        // OPTIMIZATION: More aggressive compression for better performance
        const maxWidth = 1200; // Reduced from 1920
        const maxHeight = 1200; // Reduced from 1920
        
        let { width, height } = img;
        
        // Resize if needed
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }


        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // OPTIMIZATION: Lower quality for better performance, still good for analysis
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        }, 'image/jpeg', 0.8); // Reduced from 0.95 to 0.8 for better performance
      };

      img.onerror = () => {
        resolve(file);
      };
      
      // Create object URL to load the image
      img.src = URL.createObjectURL(file);
    });
  };

  // File validation function
  const validateImageFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/webp'];
    const maxSize = 50 * 1024 * 1024; // 50MB original file limit

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Please upload a valid image file (JPEG, PNG, HEIC, or WebP)');
    }

    if (file.size > maxSize) {
      throw new Error('Image file is too large. Please choose an image under 50MB.');
    }

    return true;
  };

  // Function to switch chat types
  const switchChatType = (newChatType) => {
    if (newChatType !== chatType) {
      setChatType(newChatType);
      setIsLoadingHistory(true);
      loadConversationHistory(newChatType);
    }
  };

  // ── Consent-gated upload wrappers ──
  // These intercept uploads and show the consent modal if the user hasn't consented yet.
  //
  // Authoritative, FRESH consent check at the moment of upload. We must NOT trust
  // the in-memory `imageConsent` alone — it is loaded once on mount and can lag an
  // Account Settings toggle, or diverge between anon-localStorage and registered-DB.
  // A stale `true` would let a face photo upload with the toggle OFF (privacy bug).
  // So re-read the source of truth here and FAIL CLOSED: any error / missing grant → ask.
  const fetchCurrentConsent = async () => {
    try {
      if (isAnonymous) {
        const stored = localStorage.getItem('sonmi_image_consent');
        const storedAt = localStorage.getItem('sonmi_image_consent_at');
        return stored === 'true' && !!storedAt;
      }
      if (!currentUserId) return false;
      const { data } = await supabase
        .from('user_profiles')
        .select('image_consent, image_consent_at')
        .eq('user_id', currentUserId)
        .maybeSingle();
      return data?.image_consent === true && !!data?.image_consent_at;
    } catch (err) {
      console.error('Fresh consent check failed — failing closed (will ask):', err);
      return false;
    }
  };

  // Persist the post-scan intake alongside sending it to the analysis. The
  // analysis uses it immediately (same call); this makes it stick for next time
  // — registered users to user_profiles, guests to the guest profile that gets
  // migrated on sign-in.
  // ── Registration gate ───────────────────────────────────────────────────────
  // Guests can continue chatting after the dismissible save reminder.
  const [showDismissReason, setShowDismissReason] = useState(false);
  const nextSoftPromptAt = useRef(null);
  const hardPromptTracked = useRef(false);
  const REGISTER_GATE_ENABLED = true;
  // Web conversations are unmetered; retain only the dismissible reminder.
  const REGISTER_HARD_GATE_ENABLED = false;
  const REGISTER_HARD_AFTER = 9;

  // Non-answers that reach the message list looking exactly like assistant replies.
  // There is no isError flag anywhere in this file, so they can only be spotted by
  // their text — and if we don't, three failed uploads would "earn" a registration wall.
  const NON_ANSWER_PATTERNS = [
    /^Image upload failed/i,
    /^Sorry — I hit a snag/i,
    /^Session expired/i,
    /^Loading conversation/i,
    /^Checking for your reply/i,
  ];

  // ARTIFACT-BLIND on purpose. The reverted 8a22542 keyed this on `m.text.products`,
  // but the backend sends `products: []` on both the normal and the error path, and an
  // empty array is truthy — so that gate fired on answer 1 for every guest, including
  // on error replies. Counting real answers can't have that class of bug: it never
  // inspects the payload. It also removes the coverage hole, since a guided scan, a
  // typed product analysis and a routine turn all carry empty products/shadeMatches too.
  const _isRealAnswer = (m) => {
    if (!m || m.sender !== 'assistant') return false;
    // The welcome bubble is seeded into state before the user has done anything, so
    // counting it would make "3rd answer" arrive after only 2 real replies.
    if (m.isWelcome) return false;
    // typewriterAnimate pushes the bubble at 18 characters and mutates it every 10ms
    // (_streaming true until the final tick). Without this the modal would open a
    // fraction of a second into the answer, over a sentence fragment.
    if (m._streaming || m.isStreaming) return false;
    const body = typeof m.text === 'string' ? m.text : (m.text?.answer || m.text?.advice || '');
    return !NON_ANSWER_PATTERNS.some((re) => re.test(String(body).trim()));
  };

  const completedRequestCount = (items) => {
    let awaitingAnswer = false;
    let completed = 0;
    for (const message of items) {
      if (message?.sender === 'user') {
        awaitingAnswer = true;
      } else if (awaitingAnswer && _isRealAnswer(message)) {
        completed += 1;
        awaitingAnswer = false;
      }
    }
    return completed;
  };

  useEffect(() => {
    if (!REGISTER_GATE_ENABLED || !isAnonymous) return;
    const completedHere = completedRequestCount(messages);
    const completed = recordAnonymousRequestCount(currentSessionId, completedHere);

    // The legacy hard gate stays inactive while web chat is unmetered.
    if (REGISTER_HARD_GATE_ENABLED && completed >= REGISTER_HARD_AFTER) {
      gateArmed.current = true;
      setGateActive(true);
      return;
    }

    if (nextSoftPromptAt.current === null) nextSoftPromptAt.current = readNextSavePromptAt();
    if (completed < nextSoftPromptAt.current || registrationInvite || showDismissReason || showUpgradeModal) return;
    nextSoftPromptAt.current = nextSavePromptAfter(completed);
    try { localStorage.setItem(NEXT_SAVE_PROMPT_KEY, String(nextSoftPromptAt.current)); } catch (_e) {}
    trackRegisterPromptShown(`soft_answer_${completed}`);
    setRegistrationInvite('soft');
  }, [messages, isAnonymous, currentSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (registrationInvite !== 'hard' || hardPromptTracked.current) return;
    hardPromptTracked.current = true;
    trackRegisterPromptShown(`hard_before_${REGISTER_HARD_AFTER + 1}`);
  }, [registrationInvite]);

  useEffect(() => {
    if (isAnonymous) return;
    gateArmed.current = false;
    setGateActive(false);
    setRegistrationInvite(null);
    setShowDismissReason(false);
  }, [isAnonymous]);

  // No-op: the register prompts are derived from completed request/answer pairs,
  // so per-reply call sites no longer do the counting.
  const noteAnonInteraction = () => {};

  // "Does this sound like your skin?" — accuracy, not satisfaction.
  const handleReadFeedback = (verdict) => {
    trackReadFeedback(verdict);
  };

  // A correction is a strong "I care" moment, but we no longer pop a separate
  // register ask here — the single mandatory registration moment is the
  // tenth-request gate. The "I've noted it" reply below still counts as an
  // interaction toward that gate.
  const handleCorrectionSubmit = async ({ field, note }) => {
    trackCorrectionSubmitted(field, !!note);
    try {
      if (currentUserId && !isAnonymous) {
        // Append the correction to personalization_data.corrections (append-only,
        // provenance-tagged) so the advisor can weigh it ALONGSIDE the scan and surface
        // the difference two-way — WITHOUT blowing away `notes` or any other
        // personalization_data keys. Slice 1 of DATA_ARCHITECTURE_PLAN.md.
        // (Was: a blind `notes` overwrite the advisor never read.)
        const { data: existing } = await supabase
          .from('user_profiles')
          .select('personalization_data')
          .eq('user_id', currentUserId)
          .maybeSingle();
        const existingPD = (existing && typeof existing.personalization_data === 'object' && existing.personalization_data) || {};
        const priorCorrections = Array.isArray(existingPD.corrections) ? existingPD.corrections : [];
        const corrections = [
          ...priorCorrections,
          { field, note: note || '', source: 'self_reported', at: new Date().toISOString() },
        ].slice(-20); // cap so the blob can't grow unbounded
        await supabase.from('user_profiles').upsert(
          { user_id: currentUserId, personalization_data: { ...existingPD, corrections } },
          { onConflict: 'user_id' }
        );
      } else {
        // A guest's correction must survive the session and migrate with the
        // rest of their temporary profile if they create an account later.
        const existing = getGuestProfile() || {};
        const existingPD = (existing.personalization_data && typeof existing.personalization_data === 'object')
          ? existing.personalization_data
          : {};
        const priorCorrections = Array.isArray(existingPD.corrections) ? existingPD.corrections : [];
        const corrections = [
          ...priorCorrections,
          { field, note: note || '', source: 'self_reported', at: new Date().toISOString() },
        ].slice(-20);
        localStorage.setItem('sonmi_guest_profile', JSON.stringify({
          ...existing,
          personalization_data: { ...existingPD, corrections },
        }));
      }
    } catch (err) {
      console.error('Correction persist error:', err);
    }
    setMessages(prev => [...prev, {
      text: `That's really helpful. What I saw in the scan plus what you know about your skin gives a much sharper read. I've noted it against **${field.toLowerCase()}** and I'll factor that in from here on.`,
      sender: 'assistant',
      timestamp: new Date().toISOString(),
      skipFeedback: true,
    }]);
  };

  const handleShadeMatchesRequested = () => {
    // Tracking only -- do NOT count this as an interaction. Tapping the CTA
    // sends a chat message, and that assistant reply already increments via
    // noteAnonInteraction; counting both made one tap look like two.
    trackShadeMatchesRequested('cta');
  };

  // What we know for the post-scan intake, resolved AT SCAN TIME. The preloaded
  // `scanIntakeKnown` can still be null if the profile fetch hasn't landed by the time
  // the user taps scan — which re-asked people who already had an age on file. So fall
  // back to reading the profile here before deciding to ask.
  const resolveScanIntake = async () => {
    if (scanIntakeKnown?.age_range) return scanIntakeKnown;
    try {
      if (isAnonymous || !currentUserId) {
        const raw = localStorage.getItem('sonmi_guest_profile');
        const g = raw ? JSON.parse(raw) : null;
        if (!g?.age_range) return null;
        const known = { age_range: g.age_range, sensitivities: g.sensitivities || [] };
        setScanIntakeKnown(known);
        return known;
      }
      const { data } = await supabase
        .from('user_profiles')
        .select('age_range, sensitivities')
        .eq('user_id', currentUserId)
        .maybeSingle();
      if (!data?.age_range) return null;
      const known = { age_range: data.age_range, sensitivities: data.sensitivities || [] };
      setScanIntakeKnown(known);
      return known;
    } catch (err) {
      console.error('resolveScanIntake error:', err);
      return null; // on any error fall back to asking — never block the scan
    }
  };

  const persistScanIntake = async (intake) => {
    if (!intake) return;
    const answers = {};
    if (intake.age_range) answers.age_range = intake.age_range;
    if (Array.isArray(intake.sensitivities) && intake.sensitivities.length) {
      answers.sensitivities = intake.sensitivities;
    }
    if (!Object.keys(answers).length) return;
    try {
      if (currentUserId && !isAnonymous) {
        await supabase.from('user_profiles').upsert(
          { user_id: currentUserId, ...answers },
          { onConflict: 'user_id' }
        );
      } else {
        const raw = localStorage.getItem('sonmi_guest_profile');
        const existing = raw ? JSON.parse(raw) : {};
        localStorage.setItem('sonmi_guest_profile', JSON.stringify({ ...existing, ...answers }));
      }
    } catch (err) {
      console.error('Intake persist error:', err);
    }
    // Reflect the just-saved answers so a second scan this session doesn't re-ask.
    setScanIntakeKnown((prev) => {
      const age_range = answers.age_range || prev?.age_range;
      if (!age_range) return prev;
      return { age_range, sensitivities: answers.sensitivities || prev?.sensitivities || [] };
    });
  };

  const consentGatedUploadSingle = async (file, skipLoadingMessage = false, intake, journey = null) => {
    const granted = await fetchCurrentConsent();
    setImageConsent(granted); // resync the possibly-stale in-memory flag to reality
    if (granted) {
      // intake = the post-scan age/sensitivities answers, threaded into the
      // SAME analysis request so the read is actually shaped by them.
      return uploadSingleImage(file, skipLoadingMessage, {
        ...(intake ? { intake } : {}),
        ...(journey || {}),
      });
    }
    // Not granted (or toggled off since mount) — hold the file and ask first.
    setPendingConsentFiles([file]);
    setPendingConsentIntake(intake || null);
    setPendingConsentJourney(journey || null);
    setPendingConsentPrompt('');
    setShowConsentModal(true);
  };

  // Attach-then-type product upload. Tags the request analysis_type='product' so
  // the backend runs its deterministic product analyzer (rich product_data card)
  // rather than the skin path. uploadSingleImage carries the current `input` as
  // user_prompt, so the user's typed question rides along. Consent is normally
  // already granted (gated before the file picker), so the granted branch runs.
  const consentGatedUploadProduct = async (file) => {
    const granted = await fetchCurrentConsent();
    setImageConsent(granted);
    if (granted) {
      return uploadSingleImage(file, false, {
        analysisType: 'product',
        imageRole: IMAGE_ROLES.PRODUCT_REFERENCE,
      });
    }
    setPendingConsentFiles([file]);
    setPendingConsentIntake(null);
    setPendingConsentJourney({ imageRole: IMAGE_ROLES.PRODUCT_REFERENCE, analysisType: 'product' });
    setPendingConsentPrompt('');
    setShowConsentModal(true);
  };

  // ONE "Upload a photo" entry point: the user should never have to classify
  // their own photo. We send no analysis_type (so the backend stays on 'auto')
  // plus this neutral prompt, which is what lets the model SEE the photo and
  // pick the tool itself (product / skin / shade). The prompt is backend-only —
  // it is never shown as if the user typed it. If they DID type a question,
  // theirs wins (see user_prompt in sendImageForAnalysis).
  const GENERIC_PHOTO_PROMPT =
    "Look at this photo and respond to what I am trying to do. Do not assume that a face photo " +
    "is a request for a full skin analysis. If my purpose is unclear, briefly identify what you " +
    "can help with and ask one focused question.";

  const consentGatedUploadPhoto = async (file) => {
    const granted = await fetchCurrentConsent();
    setImageConsent(granted);
    if (granted) {
      return uploadSingleImage(file, false, {
        promptOverride: GENERIC_PHOTO_PROMPT,
        imageRole: IMAGE_ROLES.GENERIC_CHAT_PHOTO,
      });
    }
    setPendingConsentFiles([file]);
    setPendingConsentIntake(null);
    setPendingConsentJourney({ imageRole: IMAGE_ROLES.GENERIC_CHAT_PHOTO });
    setPendingConsentPrompt('');
    setShowConsentModal(true);
  };

  const consentGatedUploadMultiple = async (files, context = '') => {
    const granted = await fetchCurrentConsent();
    setImageConsent(granted); // resync the possibly-stale in-memory flag to reality
    if (granted) {
      return uploadImages(files, context);
    }
    setPendingConsentFiles(Array.from(files));
    setPendingConsentPrompt(context);
    setShowConsentModal(true);
  };

  // Shows consent modal and returns a Promise<boolean> — used when user clicks Upload button
  // (so consent appears before the file picker, not after file selection)
  const requestConsent = () => {
    return new Promise((resolve) => {
      consentResolveRef.current = resolve;
      setShowConsentModal(true);
    });
  };

  const handleConsentAllow = async () => {
    setShowConsentModal(false);
    // Resolve button-click flow promise if pending
    if (consentResolveRef.current) {
      consentResolveRef.current(true);
      consentResolveRef.current = null;
    }
    await grantImageConsent();
    // Resume the upload that was waiting (drag-drop / post-selection flow)
    if (pendingConsentFiles && pendingConsentFiles.length > 0) {
      if (pendingConsentFiles.length === 1) {
        await uploadSingleImage(
          pendingConsentFiles[0], false, {
            ...(pendingConsentIntake ? { intake: pendingConsentIntake } : {}),
            ...(pendingConsentJourney || {}),
          }
        );
      } else {
        await uploadImages(pendingConsentFiles, pendingConsentPrompt);
      }
    }
    setPendingConsentFiles(null);
    setPendingConsentIntake(null);
    setPendingConsentJourney(null);
    setPendingConsentPrompt('');
  };

  const handleConsentNotNow = () => {
    trackConsentDeclined();
    setShowConsentModal(false);
    // Resolve button-click flow promise if pending
    if (consentResolveRef.current) {
      consentResolveRef.current(false);
      consentResolveRef.current = null;
    }
    setPendingConsentFiles(null);
    setPendingConsentIntake(null);
    setPendingConsentJourney(null);
    setPendingConsentPrompt('');
    // Let the user know they can't upload without consent
    setMessages(prev => [
      ...prev,
      {
        text: "No problem. You can keep chatting without photos. If you change your mind, tap the upload icon beside the message box and we’ll ask again.",
        sender: 'assistant',
        skipFeedback: true,
      }
    ]);
  };

  // Show loading while fetching history
  if (isLoadingHistory) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '16px'
      }}>
        Loading your conversation...
      </div>
    );
  }

  return (
    <>
      <ChatUI
        messages={messages}
        input={input}
        setInput={setInput}
        sendMessage={sendMessage}
        uploadImage={consentGatedUploadSingle}
        uploadImages={consentGatedUploadMultiple}
        uploadProductImage={consentGatedUploadProduct}
        uploadPhoto={consentGatedUploadPhoto}
        uploadCaptureImage={uploadCaptureImage}
        showAnalysisTypeChoice={showAnalysisTypeChoice}
        isLoading={isLoading}
        streamStatus={streamStatus}
        onRefineResults={handleRefineResults}
        onRequestUpgrade={requestUpgrade}
        uploadedImages={uploadedImages}
        analysisProgress={analysisProgress}
        showAnalyzedPhotos={showAnalyzedPhotos}
        saveProductToBackend={saveProductToBackend}
        getSavedProductsFromBackend={getSavedProductsFromBackend}
        userId={currentUserId}
        isAnonymous={isAnonymous}
        onShowSignIn={() => {
          setUpgradeStartsOnSignIn(false);
          setUpgradeSkipsPitch(true);
          setShowUpgradeModal(true);
        }}
        onPersistentSignIn={() => {
          setUpgradeStartsOnSignIn(true);
          setUpgradeSkipsPitch(true);
          setShowUpgradeModal(true);
        }}
        suggestedQuestions={(() => {
          // The assistant's follow_up is ALREADY rendered inline under the answer, so it must
          // NOT also appear as chip #1 — it read as a duplicate and truncated mid-sentence in
          // the chip row. We still filter it out of the backend chips so the same question
          // can't show up twice.
          let _fu = '';
          for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m && m.sender === 'assistant') {
              if (m.text && typeof m.text === 'object' && typeof m.text.follow_up === 'string') _fu = m.text.follow_up.trim();
              break;
            }
          }
          const _base = Array.isArray(suggestedQuestions) ? suggestedQuestions : [];
          return _fu
            ? _base.filter(q => (q?.text || '').trim().toLowerCase() !== _fu.toLowerCase())
            : _base;
        })()}
        onSuggestedQuestionClick={handleSuggestedQuestionClick}
        suggestionsDismissed={suggestionsDismissed}
        onDismissSuggestions={() => setSuggestionsDismissed(true)}
        onShowSuggestions={() => setSuggestionsDismissed(false)}
        showProfileNudge={showProfileNudge}
        profileCompleteness={profileCompleteness}
        missingFields={missingFields}
        onProfileNudgeDismiss={handleProfileNudgeDismiss}
        onCompleteProfile={handleCompleteProfile}
        onProfileSave={({ completeness }) => {
          setProfileCompleteness(completeness >= 75 ? 100 : completeness);
          setShowProfileNudge(false);
          try { localStorage.setItem('sonmi_had_analysis', 'true'); } catch {}
        }}
        chatList={chatList}
        currentSessionId={currentSessionId}
        onNewChat={handleNewChat}
        onSwitchChat={handleSwitchChat}
        autoTriggerUpload={autoTriggerUpload}
        onAutoTriggerUploadHandled={() => setAutoTriggerUpload(false)}
        openMenuAt={pendingMenuView}
        onOpenMenuAtHandled={() => setPendingMenuView(null)}
        onGatedCompose={reopenGate}
        gateActive={gateActive}
        imageConsent={imageConsent}
        requestConsent={requestConsent}
        onImageConsentChange={setImageConsent}
        onIntakeCollected={persistScanIntake}
        scanIntakeKnown={scanIntakeKnown}
        resolveScanIntake={resolveScanIntake}
        onReadFeedback={handleReadFeedback}
        onCorrectionSubmit={handleCorrectionSubmit}
        onShadeMatchesRequested={handleShadeMatchesRequested}
      />
      
      {/* Product education tour for new users — shown first; profile popup comes after */}
      {showProductTour && (
        <FirstTimeOverview
          isOpen={showProductTour}
          userId={currentUserId}
          isAnonymous={isAnonymous}
          onClose={() => {
            setShowProductTour(false);
          }}
          onComplete={() => {
            setShowProductTour(false);
            hasIncompleteProfileRef.current = false;
          }}
          onTriggerUpload={() => setAutoTriggerUpload(true)}
        />
      )}

      {/* Onboarding Modal — only when tour isn't showing; for logged-in users with incomplete profile */}
      {showOnboardingModal && !isAnonymous && !showProductTour && (
        <OnboardingModal
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          userId={currentUserId}
        />
      )}
      
      {/* Analysis Type Choice Modal for Multiple Images */}
      {showAnalysisChoice && pendingFiles.length > 0 && (
        <AnalysisTypeChoice
          images={pendingFiles.map((file, index) => ({
            url: previewUrls.current[index], // Use tracked URLs (will be cleaned up)
            name: file.name
          }))}
          isMultiple={true}
          imageCount={pendingFiles.length}
          userPrompt={pendingPrompt}
          onPromptChange={setPendingPrompt}
          onSelectAnalysisType={handleAnalysisTypeSelect}
          onCancel={handleAnalysisTypeCancel}
        />
      )}

      {/* Image Consent Modal — shown once before first photo upload */}
      <ImageConsentModal
        isOpen={showConsentModal}
        onAllow={handleConsentAllow}
        onNotNow={handleConsentNotNow}
      />

      {/* Son:mi+ paywall — opened only by the backend's quota response. */}
      <PlusPaywall
        isOpen={showPaywall}
        busy={paywallBusy}
        onClose={() => { setPaywallBusy(false); setShowPaywall(false); }}
        onSubscribe={startCheckout}
      />

      {REGISTER_GATE_ENABLED && registrationInvite && (
        <ContextualSaveInvitation
          variant={registrationInvite}
          onDismiss={() => {
            const dismissed = registrationInvite;
            setRegistrationInvite(null);
            trackRegisterPromptDismissed(dismissed);
            if (dismissed === 'soft' && !getHasAskedDismissReason()) {
              setTimeout(() => setShowDismissReason(true), 250);
            }
          }}
          onCreateAccount={() => {
            const fromHardGate = registrationInvite === 'hard';
            trackRegistrationAction('create_account_clicked', `${registrationInvite}_invitation`);
            setRegistrationInvite(null);
            setUpgradeStartsOnSignIn(false);
            setUpgradeSkipsPitch(true);
            setUpgradeModalMandatory(fromHardGate);
            setShowUpgradeModal(true);
          }}
          onSignIn={() => {
            const fromHardGate = registrationInvite === 'hard';
            trackRegistrationAction('sign_in_clicked', `${registrationInvite}_invitation`);
            setRegistrationInvite(null);
            setUpgradeStartsOnSignIn(true);
            setUpgradeSkipsPitch(true);
            setUpgradeModalMandatory(fromHardGate);
            setShowUpgradeModal(true);
          }}
        />
      )}

      {REGISTER_GATE_ENABLED && showDismissReason && (
        <RegistrationDismissReason
          onSelect={(reason) => {
            trackRegisterDismissReason(reason);
            setHasAskedDismissReason();
            setShowDismissReason(false);
          }}
          onSkip={() => {
            setHasAskedDismissReason();
            setShowDismissReason(false);
          }}
        />
      )}

      {/* Keep the answer they just earned on screen when the gate appears. The
          sheet reserves space via --gate-h, but the list does not re-scroll on
          its own, so without this the last message slides under the sheet and the
          gate looks like it swallowed the reply. Two frames: one after the pad
          lands, one after the sheet finishes measuring. */}
      <UpgradeAccountModal
        isOpen={showUpgradeModal}
        startOnSignIn={upgradeStartsOnSignIn}
        skipPitch={upgradeSkipsPitch}
        mandatory={upgradeModalMandatory}
        onExplore={handleExploreAfterRegister}
        previewWelcome={previewWelcome}
        savedItems={(() => {
          // Only ever name things that actually exist in THIS session — the old modal's
          // "I've seen your skin now" claimed a scan that often hadn't happened, and a
          // welcome screen that overstates is the same mistake with a friendlier face.
          const items = [];
          if (messages.some((m) => m.skinAnalysis)) items.push('Your skin read');
          if (messages.some((m) => m.shadeMatches || m.text?.shade_cards)) items.push('Your shade matches');
          const products = messages.filter(
            (m) => m.text && typeof m.text === 'object' && typeof m.text.products === 'string' && m.text.products.trim()
          ).length;
          if (products) items.push(`Products from this chat`);
          items.push('This conversation');
          return items;
        })()}
        onClose={() => {
          // Closing ALWAYS closes, even for the mandatory gate. It used to early-
          // return when mandatory, so every close control (the X, the swipe, the
          // footer) hit a no-op and the sheet felt stuck. Nothing is bypassed:
          // gateArmed stays set, so the moment they try to type it comes back.
          setShowUpgradeModal(false);
          setUpgradeStartsOnSignIn(false);
          setUpgradeSkipsPitch(false);
          setUpgradeModalMandatory(false);
        }}
        // Do NOT close on success. Registering flips the modal to its welcome view,
        // which names what was kept and offers the feature examples; closing here
        // meant that view rendered on an already-closed modal and nobody ever saw
        // it. Dropping `mandatory` is enough — they close it themselves.
        onSuccess={() => {
          setUpgradeModalMandatory(false);
          setGateActive(false);
          setRegistrationInvite(null);
          gateArmed.current = false;
          clearHasSeenProductTour();
        }}
      />
    </>
  );
};

export default ChatComponent;
